import React, { useEffect, useState, useContext, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";
import { useTenant } from "../contexts/TenantContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { 
  ArrowLeft, Save, Send, Camera, Trash2, CheckCircle, XCircle, 
  AlertTriangle, FileText, Building2, Upload, Link, ExternalLink,
  ClipboardList, Loader2, Plus, Image, X, AlertOctagon, Clock,
  User, FileCheck, ChevronDown, ChevronUp, Download, Pencil, FileDown,
  Languages, Calendar, Users, CheckSquare, Sparkles, Bot, Copy
} from "lucide-react";
import SignaturePad from "../components/SignaturePad";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Evaluation status options for RoyalCert
const evaluationOptions = [
  { value: "conform", label: "Conform", color: "bg-green-100 text-green-800 border-green-300" },
  { value: "major_nc", label: "Major NC", color: "bg-red-100 text-red-800 border-red-300" },
  { value: "minor_nc", label: "Minor NC", color: "bg-orange-100 text-orange-800 border-orange-300" },
  { value: "observation", label: "Observation", color: "bg-blue-100 text-blue-800 border-blue-300" },
  { value: "not_applicable", label: "N/A", color: "bg-gray-100 text-gray-600 border-gray-300" }
];

// FR.45 Opening Meeting Agenda Items
const OPENING_AGENDA_ITEMS = [
  { id: 1, text: "Appreciation to the Organization for Choosing RoyalCert as Its Certification Body" },
  { id: 2, text: "Introduction of the Audit Team (team leader, auditors, technical expert, and observers)" },
  { id: 3, text: "Confirmation of the Audit Standard and the Language of the Audit" },
  { id: 4, text: "Confirmation of the Certification Scope and the Number of Employees" },
  { id: 5, text: "Explanation of the Confidentiality Principle" },
  { id: 6, text: "Confirmation of Occupational Health, Emergency, and Security Procedures" },
  { id: 7, text: "Review of the Audit Plan Including Any Potential Changes" },
  { id: 8, text: "Designation of a Guide to Accompany the Audit Team" },
  { id: 9, text: "Confirmation That the Client Will Be Informed of the Audit Progress" },
  { id: 10, text: "Explanation of the Audit Methodology (Including Sampling, if Applicable)" },
  { id: 11, text: "Explanation That the Audit Team Represents RoyalCert" },
  { id: 12, text: "Review of Previous Surveillance or Audit Findings, if Applicable" },
  { id: 13, text: "Definition of Nonconformities and Observations" },
  { id: 14, text: "Explanation of the Report Preparation Process" },
  { id: 15, text: "Notification of the Rules Regarding the Presence of Consultants" },
  { id: 16, text: "Explanation of the Client Complaint Handling Procedure" },
  { id: 17, text: "Questions and Clarifications" }
];

// FR.45 Closing Meeting Agenda Items
const CLOSING_AGENDA_ITEMS = [
  { id: 1, text: "Appreciation for the Cooperation and Hospitality During the Audit" },
  { id: 2, text: "Confirmation of the Proposed Scope of Certification" },
  { id: 3, text: "Confirmation of the Confidentiality of Audit Results" },
  { id: 4, text: "Presentation of the Positive Aspects of the Organization" },
  { id: 5, text: "Explanation of Any Identified Nonconformities (If Applicable)" },
  { id: 6, text: "Explanation of Observations (If Any) and Their Relevance" },
  { id: 7, text: "Confirmation that the Audit was Conducted Using Sampling-Based Methodology" },
  { id: 8, text: "Explanation of the Certification Process" },
  { id: 9, text: "Logo Usage Instructions (If Certification is Granted)" },
  { id: 10, text: "Explanation of the Complaints and Appeals Process" },
  { id: 11, text: "Questions and Clarifications" }
];

// Helper function to format date for input fields - handles UTC to Turkey timezone
const formatDateForInput = (dateValue) => {
  if (!dateValue) return "";
  // If already in yyyy-MM-dd format, return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return dateValue;
  // If ISO datetime format, convert to Turkey timezone first
  if (dateValue.includes('T')) {
    try {
      const date = new Date(dateValue);
      // Convert to Turkey timezone (UTC+3)
      const turkeyDate = new Date(date.toLocaleString('en-US', { timeZone: 'Europe/Istanbul' }));
      const year = turkeyDate.getFullYear();
      const month = String(turkeyDate.getMonth() + 1).padStart(2, '0');
      const day = String(turkeyDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      // Fallback to simple split if date parsing fails
      return dateValue.split('T')[0];
    }
  }
  return dateValue;
};

// Helper to convert UTC date to Turkey date string
const utcToTurkeyDate = (isoDateString) => {
  if (!isoDateString) return "";
  if (!isoDateString.includes('T') && !isoDateString.includes('Z')) {
    // Already a plain date string
    return isoDateString.split(' ')[0]; // Handle "2026-02-17 - 2026-02-18" format
  }
  try {
    const date = new Date(isoDateString);
    // Format in Turkey timezone
    return date.toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' }); // sv-SE gives YYYY-MM-DD format
  } catch (e) {
    return isoDateString.split('T')[0];
  }
};

// Helper to get today's date in Turkey timezone as YYYY-MM-DD string
const getTurkeyDateString = () => {
  const now = new Date();
  // Get date parts in Turkey timezone
  const options = { timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit' };
  const parts = new Intl.DateTimeFormat('sv-SE', options).format(now);
  return parts; // Returns YYYY-MM-DD
};

// Helper to convert audit type code to readable label
const getAuditTypeLabel = (auditType) => {
  if (!auditType) return "";
  
  const auditTypeMapping = {
    // Basic types
    "initial": "Initial Certification / İlk Belgelendirme",
    "surveillance": "Surveillance / Gözetim",
    "recertification": "Re-Certification / Yeniden Belgelendirme",
    // Stage-specific types
    "initial_stage2": "Initial Certification - Stage 2 / İlk Belgelendirme - 2. Aşama",
    "initial_stage3": "Initial Certification - Stage 3 / İlk Belgelendirme - 3. Aşama",
    "surveillance_stage2": "Surveillance - Stage 2 / Gözetim - 2. Aşama",
    "surveillance_stage3": "Surveillance - Stage 3 / Gözetim - 3. Aşama",
    "recertification_stage2": "Re-Certification - Stage 2 / Yeniden Belgelendirme - 2. Aşama",
    "recertification_stage3": "Re-Certification - Stage 3 / Yeniden Belgelendirme - 3. Aşama",
    // TR types
    "tr_i": "TR-I Denetimi",
    "tr_ii": "TR-II Denetimi",
    // GSTC types
    "gstc": "GSTC Denetimi",
    "gstc_initial": "GSTC Initial Certification",
    "gstc_surveillance": "GSTC Surveillance",
    // Combined
    "3rd Stage (GSTC)": "3rd Stage (GSTC)",
    "2nd Stage (TR-I)": "2nd Stage (TR-I)",
    "Initial Certification / İlk Sertifikasyon": "Initial Certification / İlk Sertifikasyon",
    "Initial Certification / İlk Belgelendirme": "Initial Certification / İlk Belgelendirme",
    "Surveillance / Gözetim": "Surveillance / Gözetim",
    "Re-Certification / Yeniden Sertifikasyon": "Re-Certification / Yeniden Sertifikasyon",
    "Re-Certification / Yeniden Belgelendirme": "Re-Certification / Yeniden Belgelendirme"
  };
  
  return auditTypeMapping[auditType] || auditType;
};

const RoyalCertAuditForm = () => {
  const { auditId, auditRecordId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const { getPrimaryColor } = useTenant();
  
  // Check URL params for view mode
  const searchParams = new URLSearchParams(location.search);
  const viewMode = searchParams.get('view');
  const isPlanningExpertView = user?.role === 'planning_expert' && viewMode === 'documents';
  
  // Determine which ID to use
  const effectiveAuditId = auditId;
  const effectiveRecordId = auditRecordId;
  
  const [template, setTemplate] = useState(null);
  const [auditRecord, setAuditRecord] = useState(null);
  const [audit, setAudit] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // If Planning Expert with documents view, default to documents tab
  const [activeTab, setActiveTab] = useState(isPlanningExpertView ? "documents" : "checklist");
  
  // Determine audit stage based on customer's audit_phase or audit type
  const auditPhase = customer?.audit_phase || audit?.audit_type || "";
  const isStage2 = auditPhase === "2" || 
                   auditPhase.includes("stage2") || 
                   auditPhase.includes("_2") ||
                   auditPhase === "initial_stage2" ||
                   auditPhase === "surveillance_stage2" ||
                   auditPhase === "recertification_stage2";
  const isStage3 = !isStage2;
  
  // Checklist state
  const [checklistData, setChecklistData] = useState({});
  
  // Report state
  const [headerData, setHeaderData] = useState({});
  const [reportSections, setReportSections] = useState({});
  const [certificateDecision, setCertificateDecision] = useState({});
  
  // Evidence state
  const [evidenceLinks, setEvidenceLinks] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [newLink, setNewLink] = useState({ link_type: "wetransfer", url: "", description: "", criteria_ids: [] });
  
  // NCR (Nonconformity) state
  const [ncrData, setNcrData] = useState({});
  
  // Interview and Meeting Attendees state
  const [interviewRecords, setInterviewRecords] = useState([]);
  const [meetingAttendees, setMeetingAttendees] = useState([]);
  
  // Meeting details for FR.45
  const [meetingDetails, setMeetingDetails] = useState({
    openingDate: "",
    openingHour: "09:00",
    closingDate: "",
    closingHour: "17:00"
  });
  
  // Meeting agenda items - checked state
  const [openingAgendaChecked, setOpeningAgendaChecked] = useState({});
  const [closingAgendaChecked, setClosingAgendaChecked] = useState({});
  
  // Documents state
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  
  // Signed PDFs state
  const [signedPdfStatus, setSignedPdfStatus] = useState({});
  const [uploadingSignedPdf, setUploadingSignedPdf] = useState(null);
  const signedPdfInputRef = React.useRef({});
  
  // Signatures state
  const [signatures, setSignatures] = useState({
    team_leader: null,
    auditor: null,
    client_representative: null
  });
  const [savingSignatures, setSavingSignatures] = useState(false);
  
  // Translation state
  const [translatingItems, setTranslatingItems] = useState({});
  const [translatingAll, setTranslatingAll] = useState(false);
  
  // Copy from previous report state
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [approvedAudits, setApprovedAudits] = useState([]);
  const [loadingApprovedAudits, setLoadingApprovedAudits] = useState(false);
  const [selectedSourceAudit, setSelectedSourceAudit] = useState(null);
  const [selectedCopyCustomer, setSelectedCopyCustomer] = useState("all"); // "all" or customer_id
  const [allCustomers, setAllCustomers] = useState([]);
  const [copyOptions, setCopyOptions] = useState({ checklist: true, fr10: true });
  const [copying, setCopying] = useState(false);
  
  // Track unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const primaryColor = getPrimaryColor();

  // Warn user before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'Kaydedilmemiş değişiklikler var. Sayfadan ayrılmak istediğinize emin misiniz?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // If we have auditRecordId, load by that first
        if (effectiveRecordId) {
          const fullRecord = await axios.get(`${API}/royalcert-audits/${effectiveRecordId}`, { withCredentials: true });
          setAuditRecord(fullRecord.data);
          setChecklistData(fullRecord.data.checklist_data?.criteria_evaluations || {});
          
          // Get the audit using audit_id from the record
          const recordAuditId = fullRecord.data.audit_id;
          let auditData = null;
          let customerData = null;
          
          if (recordAuditId) {
            try {
              const auditRes = await axios.get(`${API}/audits/${recordAuditId}`, { withCredentials: true });
              auditData = auditRes.data;
              setAudit(auditData);
              
              // Also try to get fresh customer data
              if (auditData?.customer_id) {
                try {
                  const customerRes = await axios.get(`${API}/customers/${auditData.customer_id}`, { withCredentials: true });
                  customerData = customerRes.data;
                  setCustomer(customerRes.data);  // Store customer in state for stage detection
                } catch (e) {
                  console.log("Could not fetch customer details");
                }
              }
            } catch (e) {
              console.log("Could not fetch audit details");
            }
          }
          
          // Load header data - prioritize existing saved data, then fresh data, then auto_fill
          const autoFill = fullRecord.data.auto_fill_data || {};
          const customer = autoFill.customer || {};
          const auditInfo = autoFill.audit || {};
          const planning = autoFill.planning || {};
          const existingHeader = fullRecord.data.report_data?.header_data || {};
          
          // Get customer name from multiple sources (fallback chain)
          // Priority: saved header > fresh customer data > auto_fill > record's customer_name
          const customerName = existingHeader.client_name || customerData?.name || customer.name || fullRecord.data.customer_name || "";
          const teamLeader = existingHeader.team_leader || planning.audit_team?.team_leader || auditInfo.team_leader || auditData?.assigned_auditor_name || "";
          
          // Format audit date - handle date range (start - end) with Turkey timezone
          let startDate = existingHeader.audit_date_start || auditData?.scheduled_date || auditInfo.scheduled_date || "";
          let endDate = existingHeader.audit_date_end || auditData?.end_date || auditInfo.end_date || "";
          
          // Convert UTC to Turkey timezone
          startDate = utcToTurkeyDate(startDate);
          endDate = utcToTurkeyDate(endDate);
          
          // Build audit_date as range or single date
          let auditDate = existingHeader.audit_date || "";
          if (!auditDate) {
            if (startDate && endDate && startDate !== endDate) {
              auditDate = `${startDate} - ${endDate}`;  // Date range
            } else {
              auditDate = startDate || "";  // Single date
            }
          }
          
          const auditSites = existingHeader.audit_sites || customerData?.address || customer.address || "";
          const managementRep = existingHeader.management_rep || customerData?.contact_person || customer.contact_person || "";
          const customerAddress = existingHeader.address || customerData?.address || customer.address || "";
          const roomCount = existingHeader.room_count || customerData?.room_count || customer.room_count || "";
          const employeeCount = existingHeader.employee_count || customerData?.employee_count || customer.employee_count || "";
          
          // Risk level from customer's manday calculation
          const customerRiskLevel = customerData?.risk_level || customer.risk_level || "";
          const riskLevelMapping = {
            "high_risk": "High / Yüksek",
            "low_risk": "Low / Düşük",
            "extremely_low_risk": "Extremely Low / Çok Düşük"
          };
          const mappedRiskLevel = riskLevelMapping[customerRiskLevel] || existingHeader.risk_level || "Low / Düşük";
          
          // Audit type mapping for RoyalCert (3rd Stage = GSTC)
          let auditType = existingHeader.audit_type || auditData?.audit_type || auditInfo.audit_type || "";
          if (!auditType && fullRecord.data.template_id === "royalcert_stage3_audit") {
            auditType = "3rd Stage (GSTC)";
          } else if (!auditType && fullRecord.data.template_id === "royalcert_stage2_audit") {
            auditType = "2nd Stage (TR-I)";
          }
          // Convert audit type code to readable label
          auditType = getAuditTypeLabel(auditType);
          
          // Adam-Gün: önce final_duration_days, sonra audit_duration_days, sonra customer.manday
          const auditDuration = existingHeader.audit_duration || 
                               auditData?.final_duration_days || 
                               auditData?.audit_duration_days ||
                               customerData?.manday || 
                               customer.manday || 
                               "";
          
          // Base header data with Stage 3 field IDs
          const baseHeaderData = {
            client_name: customerName,
            address: customerAddress,
            scope: existingHeader.scope || "Hotel and Accommodation Services",
            audit_criteria: existingHeader.audit_criteria || "GSTC Industry Criteria for Hotels and Accommodations V.3.0",
            management_rep: managementRep,
            room_count: roomCount,
            employee_count: employeeCount,
            audit_type: auditType,
            audit_date: auditDate,
            audit_duration: auditDuration,
            audit_sites: auditSites,
            report_date: existingHeader.report_date || getTurkeyDateString(),
            team_leader: teamLeader,
            auditor: existingHeader.auditor || planning.audit_team?.auditor_1 || "",
            technical_expert: existingHeader.technical_expert || planning.audit_team?.technical_expert || "",
            candidate_auditor: existingHeader.candidate_auditor || planning.audit_team?.auditor_2 || "",
            observer: existingHeader.observer || planning.audit_team?.observer || "",
            headquarters: existingHeader.headquarters || customerData?.address || customer.address || "",
            risk_level: mappedRiskLevel,
            // Stage 2 specific field IDs (Turkish template)
            company_name: customerName,
            brand_name: existingHeader.brand_name || customerData?.brand_name || customer.brand_name || "",
            management_rep_name: managementRep,
            management_rep_contact: existingHeader.management_rep_contact || `${customerData?.phone || customer.phone || ""} / ${customerData?.email || customer.email || ""}`,
            auditor_candidate: existingHeader.auditor_candidate || existingHeader.candidate_auditor || planning.audit_team?.auditor_2 || "",
            // New Stage 2 fields
            phone: existingHeader.phone || customerData?.phone || customer.phone || "",
            email: existingHeader.email || customerData?.email || customer.email || "",
            payment_status: existingHeader.payment_status || "Ödeme Tamamlandı"
          };
          
          setHeaderData(baseHeaderData);
          
          setReportSections(fullRecord.data.report_data?.report_sections || {});
          setCertificateDecision(fullRecord.data.report_data?.certificate_decision || {});
          setNcrData(fullRecord.data.ncr_data || {});
          setEvidenceLinks(fullRecord.data.evidence?.links || []);
          setPhotos(fullRecord.data.evidence?.photos || []);
          setInterviewRecords(fullRecord.data.interview_records || []);
          setMeetingAttendees(fullRecord.data.meeting_attendees || []);
          setMeetingDetails(fullRecord.data.meeting_details || {
            openingDate: utcToTurkeyDate(audit?.scheduled_date) || "",
            openingHour: "09:00",
            closingDate: utcToTurkeyDate(audit?.end_date || audit?.scheduled_date) || "",
            closingHour: "17:00"
          });
          setOpeningAgendaChecked(fullRecord.data.opening_agenda_checked || {});
          setClosingAgendaChecked(fullRecord.data.closing_agenda_checked || {});
          setSignatures(fullRecord.data.signatures || { team_leader: null, auditor: null, client_representative: null });
          
          // Get template based on customer's audit_phase
          const templateId = customerData?.audit_phase === "2" 
            ? "royalcert_stage2_audit" 
            : "royalcert_stage3_audit";
          const templateRes = await axios.get(`${API}/audit-templates/${templateId}`, { withCredentials: true });
          setTemplate(templateRes.data);
          
          setLoading(false);
          return;
        }
        
        // Otherwise, load by auditId (original flow)
        // Get audit info - try standard audits first, might fail for RoyalCert-only audits
        let auditData = null;
        let customerData2 = null;
        try {
          const auditRes = await axios.get(`${API}/audits/${effectiveAuditId}`, { withCredentials: true });
          auditData = auditRes.data;
          setAudit(auditData);
          
          // Get customer to determine phase
          if (auditData?.customer_id) {
            try {
              const customerRes = await axios.get(`${API}/customers/${auditData.customer_id}`, { withCredentials: true });
              customerData2 = customerRes.data;
              setCustomer(customerRes.data);
            } catch (e) {
              console.log("Could not fetch customer details");
            }
          }
        } catch (e) {
          console.log("Audit not in standard collection, continuing with RoyalCert flow");
        }
        
        // Get template based on customer's audit_phase
        const templateId2 = customerData2?.audit_phase === "2" 
          ? "royalcert_stage2_audit" 
          : "royalcert_stage3_audit";
        const templateRes = await axios.get(`${API}/audit-templates/${templateId2}`, { withCredentials: true });
        setTemplate(templateRes.data);
        
        // Check if audit record exists
        try {
          const recordRes = await axios.get(`${API}/royalcert-audits?audit_id=${effectiveAuditId}`, { withCredentials: true });
          const existingRecord = recordRes.data.find(r => r.audit_id === effectiveAuditId);
          
          if (existingRecord) {
            // Load existing record
            const fullRecord = await axios.get(`${API}/royalcert-audits/${existingRecord.audit_record_id}`, { withCredentials: true });
            setAuditRecord(fullRecord.data);
            setChecklistData(fullRecord.data.checklist_data?.criteria_evaluations || {});
            
            // Map auto_fill_data to header fields (matching FR.10 template field IDs)
            const autoFill = fullRecord.data.auto_fill_data || {};
            const customer = autoFill.customer || {};
            const auditInfo = autoFill.audit || {};
            const planning = autoFill.planning || {};
            const existingHeader = fullRecord.data.report_data?.header_data || {};
            
            // Build audit date range with Turkey timezone
            let startDate = utcToTurkeyDate(auditInfo.scheduled_date || "");
            let endDate = utcToTurkeyDate(auditInfo.end_date || "");
            let auditDateValue = existingHeader.audit_date || "";
            if (!auditDateValue) {
              auditDateValue = (startDate && endDate && startDate !== endDate) 
                ? `${startDate} - ${endDate}` 
                : startDate;
            }
            
            const mappedHeader = {
              // Client Information (Stage 3 field IDs)
              client_name: customer.name || existingHeader.client_name || "",
              address: customer.address || existingHeader.address || "",
              scope: auditInfo.scope || existingHeader.scope || "Hotel and Accommodation Services / Otel ve Konaklama Hizmetleri",
              room_count: customer.room_count || existingHeader.room_count || "",
              employee_count: customer.employee_count || existingHeader.employee_count || "",
              management_rep: customer.contact_person || existingHeader.management_rep || "",
              // Audit Information
              audit_date: auditDateValue,
              audit_duration: planning.final_duration_days || auditInfo.duration_days || existingHeader.audit_duration || customer.manday || "1.5",
              audit_sites: customer.address || existingHeader.audit_sites || "",
              report_date: existingHeader.report_date || getTurkeyDateString(),
              audit_type: getAuditTypeLabel(auditInfo.audit_type || existingHeader.audit_type) || "Initial Certification / İlk Belgelendirme",
              team_leader: planning.audit_team?.team_leader || auditInfo.team_leader || existingHeader.team_leader || "",
              auditor: planning.audit_team?.auditor || existingHeader.auditor || "",
              technical_expert: planning.audit_team?.technical_expert || existingHeader.technical_expert || "",
              audit_criteria: existingHeader.audit_criteria || "GSTC Industry Criteria for Hotels & Accommodations V.3.0",
              risk_level: customer.risk_level === "high_risk" ? "High / Yüksek" :
                         customer.risk_level === "extremely_low_risk" ? "Extremely Low / Çok Düşük" :
                         existingHeader.risk_level || "Low / Düşük",
              headquarters: customer.address || existingHeader.headquarters || "",
              // Stage 2 specific field IDs (Turkish template)
              company_name: customer.name || existingHeader.company_name || "",
              brand_name: customer.brand_name || existingHeader.brand_name || "",
              management_rep_name: customer.contact_person || existingHeader.management_rep_name || "",
              management_rep_contact: existingHeader.management_rep_contact || `${customer.phone || ""} / ${customer.email || ""}`,
              auditor_candidate: planning.audit_team?.auditor_2 || existingHeader.auditor_candidate || "",
              observer: planning.audit_team?.observer || existingHeader.observer || "",
              // New Stage 2 fields
              phone: customer.phone || existingHeader.phone || "",
              email: customer.email || existingHeader.email || "",
              payment_status: existingHeader.payment_status || "Ödeme Tamamlandı",
              ...existingHeader
            };
            setHeaderData(mappedHeader);
            
            setReportSections(fullRecord.data.report_data?.report_sections || {});
            setCertificateDecision(fullRecord.data.report_data?.certificate_decision || {});
            setEvidenceLinks(fullRecord.data.evidence?.links || []);
            setPhotos(fullRecord.data.evidence?.photos || []);
            setNcrData(fullRecord.data.ncr_data || {});
            setInterviewRecords(fullRecord.data.interview_records || []);
            setMeetingAttendees(fullRecord.data.meeting_attendees || []);
            setMeetingDetails(fullRecord.data.meeting_details || {
              openingDate: utcToTurkeyDate(auditInfo?.scheduled_date) || "",
              openingHour: "09:00",
              closingDate: utcToTurkeyDate(auditInfo?.end_date || auditInfo?.scheduled_date) || "",
              closingHour: "17:00"
            });
            setOpeningAgendaChecked(fullRecord.data.opening_agenda_checked || {});
            setClosingAgendaChecked(fullRecord.data.closing_agenda_checked || {});
            setSignatures(fullRecord.data.signatures || {
              team_leader: null,
              auditor: null,
              client_representative: null
            });
          } else {
            // Create new record
            const createRes = await axios.post(`${API}/royalcert-audits`, {
              audit_id: auditId,
              template_id: "royalcert_stage3_audit",
              title: `${auditData?.customer_name || 'Denetim'} - 3. Aşama Denetim`
            }, { withCredentials: true });
            
            setAuditRecord(createRes.data);
            
            // Map auto_fill_data for new record
            const autoFill = createRes.data.auto_fill_data || {};
            const customer = autoFill.customer || {};
            const auditInfo = autoFill.audit || {};
            const planning = autoFill.planning || {};
            
            // Build audit date range with Turkey timezone
            let startDate = utcToTurkeyDate(auditInfo.scheduled_date || "");
            let endDate = utcToTurkeyDate(auditInfo.end_date || "");
            const auditDateValue = (startDate && endDate && startDate !== endDate) 
              ? `${startDate} - ${endDate}` 
              : startDate;
            
            setHeaderData({
              client_name: customer.name || createRes.data.customer_name || "",
              address: customer.address || "",
              scope: auditInfo.scope || "Hotel and Accommodation Services / Otel ve Konaklama Hizmetleri",
              room_count: customer.room_count || "",
              employee_count: customer.employee_count || "",
              management_rep: customer.contact_person || "",
              audit_date: auditDateValue,
              audit_duration: auditInfo.duration_days || customer.manday || "1.5",
              audit_type: getAuditTypeLabel(auditInfo.audit_type) || "Initial Certification / İlk Belgelendirme",
              team_leader: planning.audit_team?.team_leader || auditInfo.team_leader || "",
              auditor: planning.audit_team?.auditor || "",
              technical_expert: planning.audit_team?.technical_expert || "",
              audit_criteria: "GSTC Industry Criteria for Hotels & Accommodations V.3.0",
              risk_level: customer.risk_level === "high_risk" ? "High / Yüksek" :
                         customer.risk_level === "extremely_low_risk" ? "Extremely Low / Çok Düşük" :
                         "Low / Düşük",
              headquarters: customer.address || "",
              audit_sites: customer.address || "",
              // Stage 2 specific field IDs
              company_name: customer.name || createRes.data.customer_name || "",
              brand_name: customer.brand_name || "",
              management_rep_name: customer.contact_person || "",
              management_rep_contact: `${customer.phone || ""} / ${customer.email || ""}`,
              auditor_candidate: planning.audit_team?.auditor_2 || "",
              observer: planning.audit_team?.observer || "",
              phone: customer.phone || "",
              email: customer.email || "",
              payment_status: "Ödeme Tamamlandı",
              report_date: getTurkeyDateString()
            });
          }
        } catch (err) {
          // Create new if not found
          const createRes = await axios.post(`${API}/royalcert-audits`, {
            audit_id: auditId,
            template_id: "royalcert_stage3_audit",
            title: `${auditData?.customer_name || 'Denetim'} - 3. Aşama Denetim`
          }, { withCredentials: true });
          
          setAuditRecord(createRes.data);
          
          // Map auto_fill_data for new record
          const autoFill = createRes.data.auto_fill_data || {};
          const customer = autoFill.customer || {};
          const auditInfo = autoFill.audit || {};
          const planning = autoFill.planning || {};
          
          // Build audit date range with Turkey timezone
          let startDate = utcToTurkeyDate(auditInfo.scheduled_date || "");
          let endDate = utcToTurkeyDate(auditInfo.end_date || "");
          const auditDateValue = (startDate && endDate && startDate !== endDate) 
            ? `${startDate} - ${endDate}` 
            : startDate;
          
          setHeaderData({
            client_name: customer.name || createRes.data.customer_name || "",
            address: customer.address || "",
            scope: auditInfo.scope || "Hotel and Accommodation Services / Otel ve Konaklama Hizmetleri",
            room_count: customer.room_count || "",
            employee_count: customer.employee_count || "",
            management_rep: customer.contact_person || "",
            audit_date: auditDateValue,
            audit_duration: auditInfo.duration_days || customer.manday || "1.5",
            audit_type: getAuditTypeLabel(auditInfo.audit_type) || "Initial Certification / İlk Belgelendirme",
            team_leader: planning.audit_team?.team_leader || auditInfo.team_leader || "",
            auditor: planning.audit_team?.auditor || "",
            technical_expert: planning.audit_team?.technical_expert || "",
            audit_criteria: "GSTC Industry Criteria for Hotels & Accommodations V.3.0",
            risk_level: customer.risk_level === "high_risk" ? "High / Yüksek" :
                       customer.risk_level === "extremely_low_risk" ? "Extremely Low / Çok Düşük" :
                       "Low / Düşük",
            headquarters: customer.address || "",
            audit_sites: customer.address || "",
            // Stage 2 specific field IDs
            company_name: customer.name || createRes.data.customer_name || "",
            brand_name: customer.brand_name || "",
            management_rep_name: customer.contact_person || "",
            management_rep_contact: `${customer.phone || ""} / ${customer.email || ""}`,
            auditor_candidate: planning.audit_team?.auditor_2 || "",
            observer: planning.audit_team?.observer || "",
            phone: customer.phone || "",
            email: customer.email || "",
            payment_status: "Ödeme Tamamlandı",
            report_date: getTurkeyDateString()
          });
        }
        
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Veriler yüklenirken hata oluştu");
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [effectiveAuditId, effectiveRecordId]);

  // Auto-fill meeting details when audit dates are available
  useEffect(() => {
    if (audit?.scheduled_date && !meetingDetails.openingDate) {
      setMeetingDetails(prev => ({
        ...prev,
        openingDate: utcToTurkeyDate(audit.scheduled_date),
        closingDate: utcToTurkeyDate(audit.end_date || audit.scheduled_date)
      }));
    }
  }, [audit?.scheduled_date, audit?.end_date]);

  // Save checklist
  const saveChecklist = useCallback(async () => {
    if (!auditRecord) return;
    
    try {
      setSaving(true);
      await axios.put(
        `${API}/royalcert-audits/${auditRecord.audit_record_id}/checklist`,
        { criteria_evaluations: checklistData },
        { withCredentials: true }
      );
      setHasUnsavedChanges(false);
      toast.success("Checklist kaydedildi");
    } catch (error) {
      console.error("Error saving checklist:", error);
      toast.error("Checklist kaydedilemedi");
    } finally {
      setSaving(false);
    }
  }, [auditRecord, checklistData]);

  // Save report
  const saveReport = useCallback(async () => {
    if (!auditRecord) return;
    
    try {
      setSaving(true);
      await axios.put(
        `${API}/royalcert-audits/${auditRecord.audit_record_id}/report`,
        { 
          header_data: headerData,
          report_sections: reportSections,
          certificate_decision: certificateDecision,
          interview_records: interviewRecords,
          meeting_attendees: meetingAttendees,
          meeting_details: meetingDetails,
          opening_agenda_checked: openingAgendaChecked,
          closing_agenda_checked: closingAgendaChecked
        },
        { withCredentials: true }
      );
      setHasUnsavedChanges(false);
      toast.success("Rapor kaydedildi");
    } catch (error) {
      console.error("Error saving report:", error);
      toast.error("Rapor kaydedilemedi");
    } finally {
      setSaving(false);
    }
  }, [auditRecord, headerData, reportSections, certificateDecision, interviewRecords, meetingAttendees, meetingDetails, openingAgendaChecked, closingAgendaChecked]);

  // Save NCR data
  const saveNcrData = useCallback(async () => {
    if (!auditRecord) return;
    
    try {
      setSaving(true);
      await axios.put(
        `${API}/royalcert-audits/${auditRecord.audit_record_id}/ncr`,
        { ncr_responses: ncrData },
        { withCredentials: true }
      );
      setHasUnsavedChanges(false);
      toast.success("Uygunsuzluk verileri kaydedildi");
    } catch (error) {
      console.error("Error saving NCR data:", error);
      toast.error("Uygunsuzluk verileri kaydedilemedi");
    } finally {
      setSaving(false);
    }
  }, [auditRecord, ncrData]);

  // Load approved audits for copying
  // Load all customers for copy dialog
  const loadAllCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers`, { withCredentials: true });
      setAllCustomers(response.data);
    } catch (error) {
      console.error("Error loading customers:", error);
    }
  }, []);

  const loadApprovedAudits = useCallback(async (selectedCustomerId = null) => {
    try {
      setLoadingApprovedAudits(true);
      // Determine current stage (2 or 3)
      const currentStage = isStage2 ? "2" : "3";
      
      // Build query params
      let url = `${API}/royalcert-audits/approved/list?stage=${currentStage}`;
      if (selectedCustomerId && selectedCustomerId !== "all") {
        url += `&customer_id=${selectedCustomerId}`;
      }
      
      const response = await axios.get(url, { withCredentials: true });
      // Filter out current audit
      const filtered = response.data.filter(a => a.audit_record_id !== auditRecord?.audit_record_id);
      setApprovedAudits(filtered);
    } catch (error) {
      console.error("Error loading approved audits:", error);
      toast.error("Onaylı denetimler yüklenemedi");
    } finally {
      setLoadingApprovedAudits(false);
    }
  }, [isStage2, auditRecord?.audit_record_id]);

  // Copy data from approved audit
  const copyFromApprovedAudit = async () => {
    if (!selectedSourceAudit || !auditRecord) return;
    
    try {
      setCopying(true);
      const response = await axios.post(
        `${API}/royalcert-audits/${auditRecord.audit_record_id}/copy-from/${selectedSourceAudit}?copy_checklist=${copyOptions.checklist}&copy_fr10=${copyOptions.fr10}`,
        {},
        { withCredentials: true }
      );
      
      toast.success(response.data.message || "Veriler kopyalandı");
      
      // Reload audit data to reflect changes
      const fullRecordRes = await axios.get(
        `${API}/royalcert-audits/${auditRecord.audit_record_id}/full`,
        { withCredentials: true }
      );
      
      if (fullRecordRes.data) {
        const fullRecord = fullRecordRes.data;
        setChecklistData(fullRecord.checklist_data?.criteria_evaluations || {});
        setReportSections(fullRecord.report_data?.report_sections || {});
      }
      
      setShowCopyDialog(false);
      setSelectedSourceAudit(null);
    } catch (error) {
      console.error("Error copying data:", error);
      toast.error(error.response?.data?.detail || "Veriler kopyalanamadı");
    } finally {
      setCopying(false);
    }
  };

  // Submit report for approval
  const [submitting, setSubmitting] = useState(false);
  
  const handleSubmitForApproval = async () => {
    if (!auditRecord?.audit_record_id) {
      toast.error("Denetim kaydı bulunamadı");
      return;
    }
    
    // Check if there are evaluations
    const evaluationCount = Object.values(checklistData).filter(e => e.status).length;
    if (evaluationCount === 0) {
      toast.error("Onaya göndermeden önce en az bir kriter değerlendirmesi yapılmalıdır");
      return;
    }
    
    // Confirm submission
    if (!window.confirm("Raporu onaya göndermek istediğinizden emin misiniz? Gönderildikten sonra düzenleyemezsiniz.")) {
      return;
    }
    
    try {
      setSubmitting(true);
      
      // First save all data
      await axios.put(
        `${API}/royalcert-audits/${auditRecord.audit_record_id}/checklist`,
        { criteria_evaluations: checklistData },
        { withCredentials: true }
      );
      
      await axios.put(
        `${API}/royalcert-audits/${auditRecord.audit_record_id}/report`,
        { 
          header_data: headerData,
          report_sections: reportSections,
          certificate_decision: certificateDecision,
          interview_records: interviewRecords,
          meeting_attendees: meetingAttendees,
          meeting_details: meetingDetails,
          opening_agenda_checked: openingAgendaChecked,
          closing_agenda_checked: closingAgendaChecked
        },
        { withCredentials: true }
      );
      
      // Submit for approval
      await axios.patch(
        `${API}/royalcert-audits/${auditRecord.audit_record_id}/submit`,
        {},
        { withCredentials: true }
      );
      
      toast.success("Rapor onaya gönderildi");
      
      // Refresh data - reload the page data
      window.location.reload();
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.response?.data?.detail || "Onaya gönderme başarısız");
    } finally {
      setSubmitting(false);
    }
  };

  // AI Review function
  // Interview record functions
  const addInterview = () => {
    setInterviewRecords([...interviewRecords, { name: "", position: "", department: "", notes: "" }]);
    setHasUnsavedChanges(true);
  };

  const updateInterview = (index, field, value) => {
    const updated = [...interviewRecords];
    updated[index][field] = value;
    setInterviewRecords(updated);
    setHasUnsavedChanges(true);
  };

  const removeInterview = (index) => {
    setInterviewRecords(interviewRecords.filter((_, i) => i !== index));
    setHasUnsavedChanges(true);
  };

  // Meeting attendee functions
  const addAttendee = () => {
    setMeetingAttendees([...meetingAttendees, { name: "", surname: "", title: "" }]);
    setHasUnsavedChanges(true);
  };

  const updateAttendee = (index, field, value) => {
    const updated = [...meetingAttendees];
    updated[index][field] = value;
    setMeetingAttendees(updated);
    setHasUnsavedChanges(true);
  };

  const removeAttendee = (index) => {
    setMeetingAttendees(meetingAttendees.filter((_, i) => i !== index));
    setHasUnsavedChanges(true);
  };

  // Download FR.45 with meeting data
  const handleDownloadFR45 = async () => {
    if (!audit?.audit_id) {
      toast.error("Denetim bilgisi bulunamadı");
      return;
    }
    
    // First save the current meeting data
    try {
      await axios.put(
        `${API}/royalcert-audits/${auditRecord?.audit_record_id}/report`,
        { 
          header_data: headerData,
          report_sections: reportSections,
          certificate_decision: certificateDecision,
          interview_records: interviewRecords,
          meeting_attendees: meetingAttendees,
          meeting_details: meetingDetails,
          opening_agenda_checked: openingAgendaChecked,
          closing_agenda_checked: closingAgendaChecked
        },
        { withCredentials: true }
      );
    } catch (error) {
      console.error("Auto-save before download failed:", error);
    }
    
    try {
      // Transform attendees to match backend format
      const participants = meetingAttendees.map(a => ({
        name: `${a.name || ""} ${a.surname || ""}`.trim(),
        role: a.title || ""
      }));
      
      const response = await axios.post(
        `${API}/royalcert/forms/FR.45/download-with-data/${audit.audit_id}`,
        {
          meeting_participants: participants,
          meeting_details: meetingDetails,
          opening_agenda_checked: openingAgendaChecked,
          closing_agenda_checked: closingAgendaChecked
        },
        { withCredentials: true, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      }));
      const link = document.createElement('a');
      link.href = url;
      const customerName = audit?.customer_name || 'Toplanti';
      link.setAttribute('download', `FR.45_${customerName.replace(/[^a-zA-Z0-9]/g, '_')}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("FR.45 Toplantı Tutanağı indirildi");
    } catch (error) {
      console.error("FR.45 download error:", error);
      toast.error(error.response?.data?.detail || "FR.45 indirilemedi");
    }
  };

  // Download FR.45 as PDF
  const handleDownloadFR45PDF = async () => {
    if (!audit?.audit_id) {
      toast.error("Denetim bilgisi bulunamadı");
      return;
    }
    
    setDownloadingDoc("FR.45_pdf");
    
    // First save the current meeting data
    try {
      await axios.put(
        `${API}/royalcert-audits/${auditRecord?.audit_record_id}/report`,
        { 
          header_data: headerData,
          report_sections: reportSections,
          certificate_decision: certificateDecision,
          interview_records: interviewRecords,
          meeting_attendees: meetingAttendees,
          meeting_details: meetingDetails,
          opening_agenda_checked: openingAgendaChecked,
          closing_agenda_checked: closingAgendaChecked
        },
        { withCredentials: true }
      );
    } catch (error) {
      console.error("Auto-save before download failed:", error);
    }
    
    try {
      // Transform attendees to match backend format
      const participants = meetingAttendees.map(a => ({
        name: `${a.name || ""} ${a.surname || ""}`.trim(),
        role: a.title || ""
      }));
      
      const response = await axios.post(
        `${API}/royalcert/forms/FR.45/pdf-with-data/${audit.audit_id}`,
        {
          meeting_participants: participants,
          meeting_details: meetingDetails,
          opening_agenda_checked: openingAgendaChecked,
          closing_agenda_checked: closingAgendaChecked
        },
        { withCredentials: true, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data], { 
        type: 'application/pdf' 
      }));
      const link = document.createElement('a');
      link.href = url;
      const customerName = audit?.customer_name || 'Toplanti';
      link.setAttribute('download', `FR.45_${customerName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("FR.45 PDF indirildi");
    } catch (error) {
      console.error("FR.45 PDF download error:", error);
      toast.error(error.response?.data?.detail || "FR.45 PDF indirilemedi");
    } finally {
      setDownloadingDoc(null);
    }
  };

  // Save signatures
  const saveSignatures = async (newSignatures) => {
    try {
      setSavingSignatures(true);
      await axios.put(
        `${API}/royalcert-audits/${auditRecord?.audit_record_id}/signatures`,
        { signatures: newSignatures },
        { withCredentials: true }
      );
      setSignatures(newSignatures);
      toast.success("İmzalar kaydedildi");
    } catch (error) {
      console.error("Signature save error:", error);
      toast.error("İmzalar kaydedilemedi");
    } finally {
      setSavingSignatures(false);
    }
  };

  const handleSignatureSave = (signatureKey, signatureData) => {
    const newSignatures = { ...signatures, [signatureKey]: signatureData };
    saveSignatures(newSignatures);
  };

  // ============ SIGNED PDF FUNCTIONS ============
  
  // Load signed PDF status
  const loadSignedPdfStatus = useCallback(async () => {
    if (!audit?.audit_id) return;
    try {
      const response = await axios.get(
        `${API}/royalcert/forms/signed-pdfs/${audit.audit_id}`,
        { withCredentials: true }
      );
      setSignedPdfStatus(response.data);
    } catch (error) {
      console.error("Failed to load signed PDF status:", error);
    }
  }, [audit?.audit_id]);

  // Load signed PDF status when audit loads
  useEffect(() => {
    if (audit?.audit_id) {
      loadSignedPdfStatus();
    }
  }, [audit?.audit_id, loadSignedPdfStatus]);

  // Upload signed PDF
  const handleUploadSignedPdf = async (formId, file) => {
    if (!file || !audit?.audit_id) return;
    
    if (file.type !== 'application/pdf') {
      toast.error("Sadece PDF dosyası yüklenebilir");
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Dosya boyutu 10MB'dan büyük olamaz");
      return;
    }
    
    setUploadingSignedPdf(formId);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(
        `${API}/royalcert/forms/${formId}/signed-pdf/${audit.audit_id}`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      toast.success(`${formId} imzalı PDF yüklendi`);
      loadSignedPdfStatus();
    } catch (error) {
      console.error("Signed PDF upload error:", error);
      toast.error(error.response?.data?.detail || "İmzalı PDF yüklenemedi");
    } finally {
      setUploadingSignedPdf(null);
    }
  };

  // Download signed PDF
  const handleDownloadSignedPdf = async (formId) => {
    if (!audit?.audit_id) return;
    
    setDownloadingDoc(`${formId}_signed`);
    try {
      const response = await axios.get(
        `${API}/royalcert/forms/${formId}/signed-pdf/${audit.audit_id}`,
        { withCredentials: true, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const customerName = audit?.customer_name || 'Denetim';
      link.setAttribute('download', `${formId}_IMZALI_${customerName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${formId} imzalı PDF indirildi`);
    } catch (error) {
      console.error("Signed PDF download error:", error);
      toast.error(error.response?.data?.detail || "İmzalı PDF indirilemedi");
    } finally {
      setDownloadingDoc(null);
    }
  };

  // Delete signed PDF
  const handleDeleteSignedPdf = async (formId) => {
    if (!audit?.audit_id) return;
    
    if (!window.confirm(`${formId} imzalı PDF'i silmek istediğinize emin misiniz?`)) return;
    
    try {
      await axios.delete(
        `${API}/royalcert/forms/${formId}/signed-pdf/${audit.audit_id}`,
        { withCredentials: true }
      );
      toast.success(`${formId} imzalı PDF silindi`);
      loadSignedPdfStatus();
    } catch (error) {
      console.error("Signed PDF delete error:", error);
      toast.error("İmzalı PDF silinemedi");
    }
  };

  // Revert to draft - Super Admin and Admin only
  const handleRevertToDraft = async () => {
    if (!auditRecord?.audit_record_id) return;
    if (!window.confirm("Raporu taslağa çekmek istediğinizden emin misiniz?")) return;
    
    try {
      await axios.patch(
        `${API}/royalcert-audits/${auditRecord.audit_record_id}/revert-to-draft`,
        {},
        { withCredentials: true }
      );
      toast.success("Rapor taslağa çekildi");
      // Refresh audit record
      const response = await axios.get(`${API}/royalcert-audits/${auditRecord.audit_record_id}`, { withCredentials: true });
      setAuditRecord(response.data);
    } catch (error) {
      console.error("Revert to draft error:", error);
      toast.error(error.response?.data?.detail || "Taslağa çekme başarısız");
    }
  };

  // Render signed PDF buttons for a form
  const renderSignedPdfButtons = (formId) => (
    <>
      <input
        type="file"
        accept=".pdf"
        className="hidden"
        ref={el => signedPdfInputRef.current[formId] = el}
        onChange={(e) => {
          handleUploadSignedPdf(formId, e.target.files[0]);
          e.target.value = ''; // Reset input
        }}
      />
      {signedPdfStatus[formId]?.uploaded ? (
        <>
          <Button
            size="sm"
            variant="outline"
            className="text-green-600 border-green-300 hover:bg-green-50"
            onClick={() => handleDownloadSignedPdf(formId)}
            disabled={downloadingDoc === `${formId}_signed`}
            title="İmzalı PDF'i indir"
          >
            {downloadingDoc === `${formId}_signed` ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Download className="w-4 h-4 mr-1" />
                İmzalı İndir
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-gray-500 hover:text-red-600"
            onClick={() => handleDeleteSignedPdf(formId)}
            title="İmzalı PDF'i sil"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </>
      ) : (
        <Button
          size="sm"
          variant="outline"
          className="text-blue-600 border-blue-300 hover:bg-blue-50"
          onClick={() => signedPdfInputRef.current[formId]?.click()}
          disabled={uploadingSignedPdf === formId}
        >
          {uploadingSignedPdf === formId ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Upload className="w-4 h-4 mr-1" />
              İmzalı Yükle
            </>
          )}
        </Button>
      )}
    </>
  );

  // Translate single evidence field (TR -> EN)
  const handleTranslateEvidence = async (criteriaId) => {
    const evidence = checklistData[criteriaId]?.evidence;
    if (!evidence || !evidence.trim()) {
      toast.error("Çevrilecek metin yok");
      return;
    }
    
    setTranslatingItems(prev => ({ ...prev, [criteriaId]: true }));
    try {
      const response = await axios.post(`${API}/translate`, { text: evidence }, { withCredentials: true });
      setChecklistData(prev => ({
        ...prev,
        [criteriaId]: { ...prev[criteriaId], evidence_details_en: response.data.translated }
      }));
      toast.success("Çeviri tamamlandı");
    } catch (error) {
      toast.error("Çeviri başarısız");
    } finally {
      setTranslatingItems(prev => ({ ...prev, [criteriaId]: false }));
    }
  };

  // Translate notes (NC description) to English
  const handleTranslateNotes = async (criteriaId) => {
    const notes = checklistData[criteriaId]?.notes;
    if (!notes || !notes.trim()) {
      toast.error("Çevrilecek metin yok");
      return;
    }
    
    setTranslatingItems(prev => ({ ...prev, [`${criteriaId}_notes`]: true }));
    try {
      const response = await axios.post(`${API}/translate`, { text: notes }, { withCredentials: true });
      setChecklistData(prev => ({
        ...prev,
        [criteriaId]: { ...prev[criteriaId], notes_en: response.data.translated }
      }));
      toast.success("Çeviri tamamlandı");
    } catch (error) {
      toast.error("Çeviri başarısız");
    } finally {
      setTranslatingItems(prev => ({ ...prev, [`${criteriaId}_notes`]: false }));
    }
  };

  // Translate all evidence fields at once
  const handleTranslateAll = async () => {
    if (!auditRecord?.audit_record_id) {
      toast.error("Önce formu kaydedin");
      return;
    }
    
    // Collect all items that need translation
    const itemsToTranslate = [];
    
    Object.entries(checklistData).forEach(([criteriaId, data]) => {
      // 1. Main evaluation (ana madde değerlendirmesi)
      if (data?.main_evaluation && data.main_evaluation.trim() && !data?.main_evaluation_en) {
        itemsToTranslate.push({ id: criteriaId, field: 'main_evaluation', text: data.main_evaluation });
      }
      
      // 2. Notes (uygunsuzluk açıklaması)
      if (data?.notes && data.notes.trim() && !data?.notes_en) {
        itemsToTranslate.push({ id: criteriaId, field: 'notes', text: data.notes });
      }
      
      // 3. Sub-answers (alt madde cevapları)
      if (data?.sub_answers && Array.isArray(data.sub_answers)) {
        const subAnswersEn = data.sub_answers_en || [];
        data.sub_answers.forEach((answer, idx) => {
          if (answer && answer.trim() && !subAnswersEn[idx]) {
            itemsToTranslate.push({ 
              id: criteriaId, 
              field: 'sub_answer', 
              subIndex: idx, 
              text: answer 
            });
          }
        });
      }
    });
    
    if (itemsToTranslate.length === 0) {
      toast.info("Çevrilecek metin bulunamadı veya tümü zaten çevrildi");
      return;
    }
    
    setTranslatingAll(true);
    try {
      const response = await axios.post(
        `${API}/translate/batch`,
        { texts: itemsToTranslate.map(item => item.text) },
        { withCredentials: true }
      );
      
      // Update state with translations
      const translations = response.data.translations;
      const newChecklistData = { ...checklistData };
      
      itemsToTranslate.forEach((item, index) => {
        if (translations[index]) {
          if (item.field === 'main_evaluation') {
            newChecklistData[item.id] = {
              ...newChecklistData[item.id],
              main_evaluation_en: translations[index].translated
            };
          } else if (item.field === 'notes') {
            newChecklistData[item.id] = {
              ...newChecklistData[item.id],
              notes_en: translations[index].translated
            };
          } else if (item.field === 'sub_answer') {
            const currentSubAnswersEn = newChecklistData[item.id]?.sub_answers_en || [];
            const newSubAnswersEn = [...currentSubAnswersEn];
            newSubAnswersEn[item.subIndex] = translations[index].translated;
            newChecklistData[item.id] = {
              ...newChecklistData[item.id],
              sub_answers_en: newSubAnswersEn
            };
          }
        }
      });
      
      setChecklistData(newChecklistData);
      
      // Auto-save after translation
      await saveChecklist();
      
      // Count translations by type
      const mainCount = itemsToTranslate.filter(i => i.field === 'main_evaluation').length;
      const notesCount = itemsToTranslate.filter(i => i.field === 'notes').length;
      const subCount = itemsToTranslate.filter(i => i.field === 'sub_answer').length;
      
      let message = `Çeviri tamamlandı: `;
      const parts = [];
      if (mainCount > 0) parts.push(`${mainCount} ana değerlendirme`);
      if (notesCount > 0) parts.push(`${notesCount} uygunsuzluk açıklaması`);
      if (subCount > 0) parts.push(`${subCount} alt madde`);
      message += parts.join(', ');
      
      toast.success(message);
    } catch (error) {
      console.error("Batch translation error:", error);
      toast.error("Toplu çeviri başarısız: " + (error.response?.data?.detail || error.message));
    } finally {
      setTranslatingAll(false);
    }
  };

  // Translate a single report section field
  const handleTranslateSection = async (fieldId) => {
    const text = reportSections[fieldId];
    if (!text || !text.trim()) {
      toast.error("Çevrilecek metin yok");
      return;
    }
    
    setTranslatingItems(prev => ({ ...prev, [`section_${fieldId}`]: true }));
    try {
      const response = await axios.post(`${API}/translate`, { text }, { withCredentials: true });
      // Store English translation with _en suffix
      setReportSections(prev => ({
        ...prev,
        [`${fieldId}_en`]: response.data.translated
      }));
      toast.success("Çeviri tamamlandı");
    } catch (error) {
      toast.error("Çeviri başarısız");
    } finally {
      setTranslatingItems(prev => ({ ...prev, [`section_${fieldId}`]: false }));
    }
  };

  // Translate all report sections at once
  const handleTranslateAllSections = async () => {
    // Collect all section fields that have Turkish text but no English translation
    const itemsToTranslate = [];
    
    template?.report?.report_sections?.forEach((section) => {
      section.fields?.forEach((field) => {
        if (field.type === "textarea") {
          const text = reportSections[field.id];
          const hasEnglish = reportSections[`${field.id}_en`];
          if (text && text.trim() && !hasEnglish) {
            itemsToTranslate.push({ id: field.id, text });
          }
        }
      });
    });
    
    if (itemsToTranslate.length === 0) {
      toast.info("Çevrilecek metin bulunamadı veya tümü zaten çevrildi");
      return;
    }
    
    setTranslatingAll(true);
    try {
      const response = await axios.post(
        `${API}/translate/batch`,
        { texts: itemsToTranslate.map(item => item.text) },
        { withCredentials: true }
      );
      
      // Update state with translations
      const translations = response.data.translations;
      const newReportSections = { ...reportSections };
      itemsToTranslate.forEach((item, index) => {
        if (translations[index]) {
          newReportSections[`${item.id}_en`] = translations[index].translated;
        }
      });
      setReportSections(newReportSections);
      
      toast.success(`${translations.length} rapor bölümü çevrildi`);
    } catch (error) {
      toast.error("Toplu çeviri başarısız");
    } finally {
      setTranslatingAll(false);
    }
  };

  // Download filled document (Word)
  const downloadDocument = async (formId) => {
    try {
      setDownloadingDoc(formId);
      
      // Use audit_id from record or from URL params
      const downloadAuditId = auditRecord?.audit_id || effectiveAuditId;
      
      if (!downloadAuditId) {
        toast.error("Denetim ID bulunamadı");
        return;
      }
      
      let response;
      
      // Special handling for FR.45 - use POST with meeting data
      if (formId === "FR.45") {
        // Format participants for FR.45
        const formattedParticipants = meetingAttendees.map(a => ({
          name: `${a.name || ''} ${a.surname || ''}`.trim(),
          role: a.title || ''
        }));
        
        // Build opening agenda checked object
        const openingChecked = {};
        OPENING_AGENDA_ITEMS.forEach(item => {
          openingChecked[item.id] = openingAgendaChecked[item.id] || false;
        });
        
        // Build closing agenda checked object
        const closingChecked = {};
        CLOSING_AGENDA_ITEMS.forEach(item => {
          closingChecked[item.id] = closingAgendaChecked[item.id] || false;
        });
        
        response = await axios.post(
          `${API}/royalcert/forms/FR.45/download-with-data/${downloadAuditId}`,
          {
            meeting_participants: formattedParticipants,
            meeting_details: meetingDetails,
            opening_agenda_checked: openingChecked,
            closing_agenda_checked: closingChecked
          },
          { 
            withCredentials: true,
            responseType: 'blob'
          }
        );
      } else if (formId === "FR.06") {
        // FR.06 uses same meeting logic as FR.45 (Stage 2 version)
        const formattedParticipants = meetingAttendees.map(a => ({
          name: a.name || "",
          role: a.title || ""
        }));
        
        // Build opening agenda checked object
        const openingChecked = {};
        OPENING_AGENDA_ITEMS.forEach(item => {
          openingChecked[item.id] = openingAgendaChecked[item.id] || false;
        });
        
        // Build closing agenda checked object
        const closingChecked = {};
        CLOSING_AGENDA_ITEMS.forEach(item => {
          closingChecked[item.id] = closingAgendaChecked[item.id] || false;
        });
        
        response = await axios.post(
          `${API}/royalcert/forms/FR.06/download-with-data/${downloadAuditId}`,
          {
            meeting_participants: formattedParticipants,
            meeting_details: meetingDetails,
            opening_agenda_checked: openingChecked,
            closing_agenda_checked: closingChecked
          },
          { 
            withCredentials: true,
            responseType: 'blob'
          }
        );
      } else if (formId === "FR.07") {
        // Special handling for FR.07 NCR Form - send NC data
        const ncs = getNonconformities();
        
        response = await axios.post(
          `${API}/royalcert/forms/FR.07/download-with-data/${downloadAuditId}`,
          {
            ncs: ncs,
            ncr_data: ncrData
          },
          { 
            withCredentials: true,
            responseType: 'blob'
          }
        );
      } else {
        // Standard GET download for other forms
        response = await axios.get(
          `${API}/royalcert/forms/${formId}/download/${downloadAuditId}`,
          { 
            withCredentials: true,
            responseType: 'blob'
          }
        );
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${formId}_${audit?.customer_name || auditRecord?.customer_name || 'audit'}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${formId} belgesi indirildi (Word)`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Belge indirilemedi");
    } finally {
      setDownloadingDoc(null);
    }
  };

  // Download PDF document
  const downloadPDF = async (formId) => {
    try {
      setDownloadingDoc(`${formId}_pdf`);
      
      const downloadAuditId = auditRecord?.audit_id || effectiveAuditId;
      
      if (!downloadAuditId) {
        toast.error("Denetim ID bulunamadı");
        return;
      }
      
      const response = await axios.get(
        `${API}/royalcert/forms/${formId}/pdf/${downloadAuditId}`,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${formId}_${audit?.customer_name || 'audit'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${formId} PDF indirildi`);
    } catch (error) {
      console.error("PDF download error:", error);
      toast.error("PDF indirilemedi");
    } finally {
      setDownloadingDoc(null);
    }
  };

  // Get nonconformities from checklist
  const getNonconformities = useCallback(() => {
    const ncs = [];
    if (!template?.checklist?.sections) return ncs;
    
    template.checklist.sections.forEach(section => {
      section.criteria.forEach(criteria => {
        const evaluation = checklistData[criteria.id];
        if (evaluation?.status === 'major_nc' || evaluation?.status === 'minor_nc') {
          ncs.push({
            criteria_id: criteria.id,
            criteria_title: criteria.title,
            section_title: section.title,
            classification: evaluation.status,
            description: evaluation.notes || '',
            evidence: evaluation.evidence || ''
          });
        }
      });
    });
    
    return ncs;
  }, [template, checklistData]);

  // Update NCR response for a specific criteria
  const updateNcrResponse = (criteriaId, field, value) => {
    setNcrData(prev => ({
      ...prev,
      [criteriaId]: {
        ...prev[criteriaId],
        [field]: value
      }
    }));
  };

  // Add evidence link
  const addEvidenceLink = async () => {
    if (!newLink.url) {
      toast.error("Link URL'si gerekli");
      return;
    }
    
    try {
      const res = await axios.post(
        `${API}/royalcert-audits/${auditRecord.audit_record_id}/evidence-link`,
        newLink,
        { withCredentials: true }
      );
      setEvidenceLinks([...evidenceLinks, res.data.link]);
      setNewLink({ link_type: "wetransfer", url: "", description: "", criteria_ids: [] });
      toast.success("Link eklendi");
    } catch (error) {
      toast.error("Link eklenemedi");
    }
  };

  // Upload photo
  const handlePhotoUpload = async (e, criteriaId = null) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Dosya boyutu 25MB'dan büyük olamaz");
      return;
    }
    
    try {
      setUploadingPhoto(true);
      const formData = new FormData();
      formData.append("file", file);
      if (criteriaId) formData.append("criteria_id", criteriaId);
      formData.append("description", "");
      
      const res = await axios.post(
        `${API}/royalcert-audits/${auditRecord.audit_record_id}/photo`,
        formData,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      
      setPhotos([...photos, res.data.photo]);
      toast.success("Fotoğraf yüklendi");
    } catch (error) {
      toast.error("Fotoğraf yüklenemedi");
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Delete photo
  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm("Bu fotoğrafı silmek istediğinize emin misiniz?")) return;
    
    try {
      await axios.delete(
        `${API}/royalcert-audits/${auditRecord.audit_record_id}/photo/${photoId}`,
        { withCredentials: true }
      );
      setPhotos(photos.filter(p => p.photo_id !== photoId));
      toast.success("Fotoğraf silindi");
    } catch (error) {
      toast.error("Fotoğraf silinemedi");
    }
  };

  // View photo in modal
  const handleViewPhoto = async (photo) => {
    try {
      // If photo already has data, use it
      if (photo.data) {
        setSelectedPhoto(photo);
        return;
      }
      
      // Otherwise fetch from server
      const res = await axios.get(
        `${API}/royalcert-audits/${auditRecord.audit_record_id}/photo/${photo.photo_id}`,
        { withCredentials: true }
      );
      setSelectedPhoto(res.data);
    } catch (error) {
      toast.error("Fotoğraf yüklenemedi");
    }
  };

  // Update checklist criteria
  const updateCriteria = (criteriaId, field, value) => {
    setChecklistData(prev => ({
      ...prev,
      [criteriaId]: {
        ...prev[criteriaId],
        [field]: value
      }
    }));
    setHasUnsavedChanges(true);
  };

  // Calculate checklist summary
  const getChecklistSummary = () => {
    const values = Object.values(checklistData);
    return {
      conform: values.filter(v => v.status === "conform").length,
      major_nc: values.filter(v => v.status === "major_nc").length,
      minor_nc: values.filter(v => v.status === "minor_nc").length,
      observation: values.filter(v => v.status === "observation").length,
      total: template?.checklist?.sections?.reduce((acc, s) => acc + s.criteria.length, 0) || 0
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: primaryColor }} />
      </div>
    );
  }

  const summary = getChecklistSummary();

  // Status config for display
  const statusConfig = {
    draft: { label: "Taslak", color: "bg-gray-100 text-gray-800" },
    submitted: { label: "Onay Bekliyor", color: "bg-blue-100 text-blue-800" },
    under_review: { label: "İnceleniyor", color: "bg-yellow-100 text-yellow-800" },
    approved: { label: "Onaylandı", color: "bg-green-100 text-green-800" },
    rejected: { label: "Reddedildi", color: "bg-red-100 text-red-800" },
    in_progress: { label: "Devam Ediyor", color: "bg-purple-100 text-purple-800" }
  };

  const currentStatus = auditRecord?.status || "draft";
  // Admin/Super Admin can revert any, Auditor can revert own
  const canRevertToDraft = ["submitted", "under_review"].includes(currentStatus) && (
    ["super_admin", "admin"].includes(user?.role) || 
    (user?.role === "auditor" && auditRecord?.created_by === user?.user_id)
  );

  // Planning expert can only see documents tab
  const isPlanningExpert = user?.role === "planning_expert";
  // Planning Expert with documents view OR just Planning Expert role
  const showLimitedView = isPlanningExpert || isPlanningExpertView;
  const canViewFullReport = !showLimitedView; // Everyone except planning expert
  const canViewEvidence = true; // Everyone can view evidence including planning expert
  
  // Can submit for approval - only super_admin OR the assigned auditor of this audit
  const isAssignedAuditor = audit?.assigned_auditor_id && audit.assigned_auditor_id === user?.user_id;
  const canSubmitForApproval = user?.role === "super_admin" || isAssignedAuditor;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Geri
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-gray-900">
                    {audit?.customer_name} - {isStage2 ? '2. Aşama Denetim' : '3. Aşama Denetim'}
                  </h1>
                  <Badge className={statusConfig[currentStatus]?.color || "bg-gray-100"}>
                    {statusConfig[currentStatus]?.label || currentStatus}
                  </Badge>
                </div>
                <p className="text-sm text-gray-500">
                  {isStage2 ? 'TR-I Endüstri Kriterleri' : 'GSTC Industry Criteria V.3.0'}
                </p>
              </div>
            </div>
            
            {/* Summary badges and actions */}
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-2">
                <Badge className="bg-green-100 text-green-800">{summary.conform} Conform</Badge>
                <Badge className="bg-red-100 text-red-800">{summary.major_nc} Major</Badge>
                <Badge className="bg-orange-100 text-orange-800">{summary.minor_nc} Minor</Badge>
                <Badge className="bg-blue-100 text-blue-800">{summary.observation} Obs</Badge>
              </div>
              
              {/* Submit for Approval button - only for assigned auditor or super_admin */}
              {canSubmitForApproval && (currentStatus === "draft" || currentStatus === "rejected") && (
                <Button 
                  variant="default"
                  size="sm"
                  onClick={handleSubmitForApproval}
                  disabled={submitting}
                  className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90 text-white"
                  data-testid="submit-for-approval-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-1" />
                  )}
                  {currentStatus === "rejected" ? "Tekrar Gönder" : "Onaya Gönder"}
                </Button>
              )}
              
              {/* Revert to Draft button for admins */}
              {canRevertToDraft && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleRevertToDraft}
                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                >
                  Taslağa Çek
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content with Tabs */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={showLimitedView ? "documents" : activeTab} onValueChange={setActiveTab}>
          <TabsList className={`grid w-full mb-6 ${showLimitedView ? 'grid-cols-2' : 'grid-cols-7'}`}>
            {canViewFullReport && (
              <>
                <TabsTrigger value="checklist" className="flex items-center gap-1 text-xs sm:text-sm">
                  <ClipboardList className="w-4 h-4" />
                  <span className="hidden sm:inline">Checklist</span>
                </TabsTrigger>
                <TabsTrigger value="report" className="flex items-center gap-1 text-xs sm:text-sm">
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline">Rapor</span>
                </TabsTrigger>
                <TabsTrigger value="interviews" className="flex items-center gap-1 text-xs sm:text-sm">
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">Mülakat</span>
                </TabsTrigger>
                <TabsTrigger value="meetings" className="flex items-center gap-1 text-xs sm:text-sm">
                  <FileCheck className="w-4 h-4" />
                  <span className="hidden sm:inline">Toplantı</span>
                </TabsTrigger>
              </>
            )}
            {/* Evidence tab - visible for all including planning expert */}
            <TabsTrigger value="evidence" className="flex items-center gap-1 text-xs sm:text-sm">
              <Image className="w-4 h-4" />
              <span className="hidden sm:inline">Kanıtlar</span>
            </TabsTrigger>
            {canViewFullReport && (
              <TabsTrigger value="ncr" className="flex items-center gap-1 text-xs sm:text-sm">
                <AlertOctagon className="w-4 h-4" />
                <span className="hidden sm:inline">NCR</span>
                {getNonconformities().length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-red-500 text-white">
                    {getNonconformities().length}
                  </span>
                )}
              </TabsTrigger>
            )}
            <TabsTrigger value="documents" className="flex items-center gap-1 text-xs sm:text-sm">
              <FileDown className="w-4 h-4" />
              <span className="hidden sm:inline">Belgeler</span>
            </TabsTrigger>
          </TabsList>

          {/* CHECKLIST TAB - Hidden for Planning Expert */}
          {canViewFullReport && (
          <TabsContent value="checklist">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{isStage2 ? 'TR-I Endüstri Kriterleri Checklist' : 'GSTC Audit Checklist'}</CardTitle>
                  <CardDescription>Her kriter için değerlendirme yapın</CardDescription>
                </div>
                <div className="flex gap-2">
                  {/* Copy from previous report button */}
                  <Button 
                    variant="outline" 
                    onClick={() => { 
                      setShowCopyDialog(true); 
                      setSelectedCopyCustomer("all");
                      setSelectedSourceAudit(null);
                      loadAllCustomers();
                      loadApprovedAudits(null);
                    }}
                    disabled={!auditRecord || auditRecord.review_status === 'approved'}
                    className="gap-2"
                    title="Önceki onaylı rapordan verileri kopyala"
                  >
                    <Copy className="w-4 h-4" />
                    <span className="hidden sm:inline">Önceki Rapordan Kopyala</span>
                  </Button>
                  {/* Translate button only for Stage 3 */}
                  {isStage3 && (
                    <Button 
                      variant="outline" 
                      onClick={handleTranslateAll} 
                      disabled={translatingAll || !auditRecord}
                      className="gap-2"
                    >
                      {translatingAll ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Languages className="w-4 h-4" />
                      )}
                      <span className="hidden sm:inline">Tümünü Çevir (TR→EN)</span>
                      <span className="sm:hidden">Çevir</span>
                    </Button>
                  )}
                  <Button onClick={saveChecklist} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Kaydet
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" className="space-y-4">
                  {template?.checklist?.sections?.map((section) => (
                    <AccordionItem key={section.id} value={section.id} className="border rounded-lg px-4">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="font-semibold">{section.title}</span>
                          <Badge variant="outline">{section.criteria.length} kriter</Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 pt-4">
                          {section.criteria.map((criteria) => (
                            <div key={criteria.id} className="border rounded-lg p-4 bg-gray-50">
                              {/* Header: Criteria ID, Title, Status */}
                              <div className="flex items-start justify-between gap-4 mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge style={{ backgroundColor: primaryColor, color: 'white' }}>
                                      {criteria.id}
                                    </Badge>
                                    <span className="font-medium">{criteria.title}</span>
                                  </div>
                                  <p className="text-sm text-gray-600">{criteria.description}</p>
                                </div>
                                <Select
                                  value={checklistData[criteria.id]?.status || ""}
                                  onValueChange={(v) => updateCriteria(criteria.id, "status", v)}
                                >
                                  <SelectTrigger className="w-40">
                                    <SelectValue placeholder="Seçin" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {evaluationOptions.map((opt) => (
                                      <SelectItem key={opt.value} value={opt.value}>
                                        <span className={`px-2 py-0.5 rounded ${opt.color}`}>
                                          {opt.label}
                                        </span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              {/* Sub-Questions Section */}
                              {criteria.questions && criteria.questions.length > 0 && (
                                <div className="mb-4 border-l-4 border-blue-200 pl-4 bg-blue-50/30 rounded-r-lg py-3">
                                  <Label className="text-xs text-blue-700 font-semibold mb-2 flex items-center gap-2">
                                    <ClipboardList className="w-4 h-4" />
                                    Alt Sorular ve Kanıtlar / Sub-Questions and Evidence ({criteria.questions.length} soru)
                                  </Label>
                                  <p className="text-xs text-blue-600 mb-3">
                                    Her soru için objektif kanıt/gözlemi ayrı ayrı girin. Tüm cevaplar raporda birleştirilecektir.
                                  </p>
                                  <div className="space-y-3">
                                    {criteria.questions.map((question, qIdx) => {
                                      // Handle both old string format and new {tr, en} object format
                                      const questionTr = typeof question === 'object' ? question.tr : question;
                                      const questionEn = typeof question === 'object' ? question.en : '';
                                      
                                      return (
                                        <div key={qIdx} className="bg-white rounded-lg p-3 border border-blue-100">
                                          <div className="flex items-start gap-2 mb-2">
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                                              {qIdx + 1}
                                            </span>
                                            <div className="flex-1">
                                              <p className="text-sm text-gray-700">{questionTr}</p>
                                              {questionEn && (
                                                <p className="text-xs text-gray-500 italic mt-1">{questionEn}</p>
                                              )}
                                            </div>
                                          </div>
                                          <div className="space-y-2">
                                            <Textarea
                                              placeholder={`Soru ${qIdx + 1} için objektif kanıt/gözlem yazın (TR)...`}
                                              value={checklistData[criteria.id]?.sub_answers?.[qIdx] || ""}
                                              onChange={(e) => {
                                                const currentSubAnswers = checklistData[criteria.id]?.sub_answers || [];
                                                const newSubAnswers = [...currentSubAnswers];
                                                newSubAnswers[qIdx] = e.target.value;
                                                updateCriteria(criteria.id, "sub_answers", newSubAnswers);
                                              }}
                                              className="h-16 text-sm"
                                            />
                                            {/* English translation for sub-answer - ONLY for Stage 3 */}
                                            {!isStage2 && (
                                            <>
                                            <div className="flex items-center gap-2">
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 px-2 text-xs"
                                                onClick={async () => {
                                                  const answer = checklistData[criteria.id]?.sub_answers?.[qIdx];
                                                  if (!answer?.trim()) {
                                                    toast.error("Çevrilecek metin yok");
                                                    return;
                                                  }
                                                  setTranslatingItems(prev => ({ ...prev, [`${criteria.id}_sub_${qIdx}`]: true }));
                                                  try {
                                                    const response = await axios.post(`${API}/translate`, { text: answer }, { withCredentials: true });
                                                    const currentSubAnswersEn = checklistData[criteria.id]?.sub_answers_en || [];
                                                    const newSubAnswersEn = [...currentSubAnswersEn];
                                                    newSubAnswersEn[qIdx] = response.data.translated;
                                                    updateCriteria(criteria.id, "sub_answers_en", newSubAnswersEn);
                                                    toast.success("Çeviri tamamlandı");
                                                  } catch (error) {
                                                    toast.error("Çeviri başarısız");
                                                  } finally {
                                                    setTranslatingItems(prev => ({ ...prev, [`${criteria.id}_sub_${qIdx}`]: false }));
                                                  }
                                                }}
                                                disabled={translatingItems[`${criteria.id}_sub_${qIdx}`] || !checklistData[criteria.id]?.sub_answers?.[qIdx]}
                                              >
                                                {translatingItems[`${criteria.id}_sub_${qIdx}`] ? (
                                                  <Loader2 className="w-3 h-3 animate-spin" />
                                                ) : (
                                                  <>
                                                    <Languages className="w-3 h-3 mr-1" />
                                                    EN
                                                  </>
                                                )}
                                              </Button>
                                              <span className="text-xs text-gray-400">İngilizceye çevir</span>
                                            </div>
                                            {checklistData[criteria.id]?.sub_answers_en?.[qIdx] && (
                                              <Textarea
                                                placeholder="English translation..."
                                                value={checklistData[criteria.id]?.sub_answers_en?.[qIdx] || ""}
                                                onChange={(e) => {
                                                  const currentSubAnswersEn = checklistData[criteria.id]?.sub_answers_en || [];
                                                  const newSubAnswersEn = [...currentSubAnswersEn];
                                                  newSubAnswersEn[qIdx] = e.target.value;
                                                  updateCriteria(criteria.id, "sub_answers_en", newSubAnswersEn);
                                                }}
                                                className="h-14 text-sm border-blue-200 bg-blue-50/50"
                                              />
                                            )}
                                            </>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              
                              {/* Main Evaluation Summary */}
                              <div className="mb-4 border-l-4 border-purple-200 pl-4 bg-purple-50/30 rounded-r-lg py-3">
                                <Label className="text-xs text-purple-700 font-semibold mb-2 flex items-center gap-2">
                                  Ana Madde Değerlendirmesi (Özet) / Main Criteria Evaluation (Summary)
                                </Label>
                                <Textarea
                                  placeholder="Bu madde için genel değerlendirme notunu yazın (TR)..."
                                  value={checklistData[criteria.id]?.main_evaluation || ""}
                                  onChange={(e) => updateCriteria(criteria.id, "main_evaluation", e.target.value)}
                                  className="mt-1 h-16 border-purple-200"
                                />
                                {/* English translation button - ONLY for Stage 3 */}
                                {!isStage2 && (
                                <div className="flex items-center gap-2 mt-2">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    onClick={async () => {
                                      const text = checklistData[criteria.id]?.main_evaluation;
                                      if (!text?.trim()) {
                                        toast.error("Çevrilecek metin yok");
                                        return;
                                      }
                                      setTranslatingItems(prev => ({ ...prev, [`${criteria.id}_main`]: true }));
                                      try {
                                        const response = await axios.post(`${API}/translate`, { text }, { withCredentials: true });
                                        updateCriteria(criteria.id, "main_evaluation_en", response.data.translated);
                                        toast.success("Çeviri tamamlandı");
                                      } catch (error) {
                                        toast.error("Çeviri başarısız");
                                      } finally {
                                        setTranslatingItems(prev => ({ ...prev, [`${criteria.id}_main`]: false }));
                                      }
                                    }}
                                    disabled={translatingItems[`${criteria.id}_main`] || !checklistData[criteria.id]?.main_evaluation}
                                  >
                                    {translatingItems[`${criteria.id}_main`] ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <>
                                        <Languages className="w-3 h-3 mr-1" />
                                        EN
                                      </>
                                    )}
                                  </Button>
                                  <span className="text-xs text-gray-400">İngilizceye çevir</span>
                                </div>
                                )}
                                {!isStage2 && checklistData[criteria.id]?.main_evaluation_en && (
                                  <Textarea
                                    placeholder="English translation..."
                                    value={checklistData[criteria.id]?.main_evaluation_en || ""}
                                    onChange={(e) => updateCriteria(criteria.id, "main_evaluation_en", e.target.value)}
                                    className="mt-2 h-14 text-sm border-blue-200 bg-blue-50/50"
                                  />
                                )}
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {/* NC Notes - only shown for nonconformities */}
                                {(checklistData[criteria.id]?.status === 'major_nc' || checklistData[criteria.id]?.status === 'minor_nc') && (
                                  <div className="md:col-span-2 border-2 border-orange-200 rounded-lg p-3 bg-orange-50/50">
                                    <Label className="text-xs text-orange-700 font-semibold flex items-center gap-1">
                                      <AlertTriangle className="w-4 h-4" />
                                      Uygunsuzluk Açıklaması (Zorunlu)
                                    </Label>
                                    <Textarea
                                      placeholder="Tespit edilen uygunsuzluğu detaylı açıklayın..."
                                      value={checklistData[criteria.id]?.notes || ""}
                                      onChange={(e) => updateCriteria(criteria.id, "notes", e.target.value)}
                                      className="mt-1 h-20 border-orange-300"
                                    />
                                    {/* EN translation for NC notes - ONLY for Stage 3 */}
                                    {!isStage2 && (
                                    <div className="flex items-center gap-2 mt-2">
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleTranslateNotes(criteria.id)}
                                        disabled={translatingItems[`${criteria.id}_notes`] || !checklistData[criteria.id]?.notes}
                                      >
                                        {translatingItems[`${criteria.id}_notes`] ? (
                                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                        ) : (
                                          <Languages className="w-3 h-3 mr-1" />
                                        )}
                                        İngilizceye Çevir
                                      </Button>
                                    </div>
                                    )}
                                    {!isStage2 && checklistData[criteria.id]?.notes_en && (
                                      <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                                        <Label className="text-xs text-blue-600 font-medium">NC Description (English)</Label>
                                        <Textarea
                                          value={checklistData[criteria.id]?.notes_en || ""}
                                          onChange={(e) => updateCriteria(criteria.id, "notes_en", e.target.value)}
                                          className="mt-1 h-16 text-sm bg-white"
                                          placeholder="English translation..."
                                        />
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Additional Notes - for non-NC or extra comments */}
                                <div className="md:col-span-2">
                                  <Label className="text-xs text-gray-500">Ek Notlar (Opsiyonel)</Label>
                                  <Textarea
                                    placeholder="Varsa ek notlar..."
                                    value={checklistData[criteria.id]?.additional_notes || ""}
                                    onChange={(e) => updateCriteria(criteria.id, "additional_notes", e.target.value)}
                                    className="mt-1 h-16"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
                
                {/* Logo Usage Evaluation Section - ONLY for Stage 3 - Collapsible */}
                {!isStage2 && (
                <Accordion type="single" collapsible className="mt-6">
                  <AccordionItem value="logo-usage" className="border-2 border-amber-200 rounded-lg bg-amber-50/30">
                    <AccordionTrigger className="px-4 py-3 hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center gap-2">
                          <FileCheck className="w-5 h-5 text-amber-800" />
                          <span className="font-semibold text-amber-800">LOGO KULLANIMININ İNCELENMESİ</span>
                          {checklistData["LOGO_USAGE"]?.status && (
                            <Badge className={evaluationOptions.find(o => o.value === checklistData["LOGO_USAGE"]?.status)?.color || "bg-gray-100"}>
                              {evaluationOptions.find(o => o.value === checklistData["LOGO_USAGE"]?.status)?.label || "-"}
                            </Badge>
                          )}
                        </div>
                        <Select
                          value={checklistData["LOGO_USAGE"]?.status || ""}
                          onValueChange={(v) => updateCriteria("LOGO_USAGE", "status", v)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <SelectTrigger className="w-32 h-8" onClick={(e) => e.stopPropagation()}>
                            <SelectValue placeholder="Seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            {evaluationOptions.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                <span className={`px-2 py-0.5 rounded ${opt.color}`}>
                                  {opt.label}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <p className="text-sm text-amber-700 mb-4">
                        TL.03 Logo Kullanım Talimatları ve Belgelendirme kılavuzlarına uygun olarak değerlendirin.
                      </p>
                  {/* Logo Usage Sub-Questions */}
                  <div className="mb-4 border-l-4 border-amber-300 pl-4 bg-white/50 rounded-r-lg py-3">
                    <Label className="text-xs text-amber-700 font-semibold mb-2 flex items-center gap-2">
                      <ClipboardList className="w-4 h-4" />
                      Alt Sorular ve Kanıtlar / Sub-Questions and Evidence (3 soru)
                    </Label>
                    <div className="space-y-3">
                      {/* Question 1 - ROYALCERT Logo */}
                      <div className="bg-white rounded-lg p-3 border border-amber-100">
                        <div className="flex items-start gap-2 mb-2">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">1</span>
                          <div className="flex-1">
                            <p className="text-sm text-gray-700">Kuruluş ROYALCERT logosunu uygun şekilde kullanıyor mu?</p>
                            <p className="text-xs text-gray-500 italic mt-1">Does the organization use the ROYALCERT logo appropriately?</p>
                          </div>
                        </div>
                        <Textarea
                          placeholder="ROYALCERT logo kullanımı hakkında gözlem yazın (TR)..."
                          value={checklistData["LOGO_USAGE"]?.sub_answers?.[0] || ""}
                          onChange={(e) => {
                            const currentSubAnswers = checklistData["LOGO_USAGE"]?.sub_answers || [];
                            const newSubAnswers = [...currentSubAnswers];
                            newSubAnswers[0] = e.target.value;
                            updateCriteria("LOGO_USAGE", "sub_answers", newSubAnswers);
                          }}
                          className="h-16 text-sm"
                        />
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={async () => {
                              const answer = checklistData["LOGO_USAGE"]?.sub_answers?.[0];
                              if (!answer?.trim()) { toast.error("Çevrilecek metin yok"); return; }
                              setTranslatingItems(prev => ({ ...prev, ["LOGO_USAGE_sub_0"]: true }));
                              try {
                                const response = await axios.post(`${API}/translate`, { text: answer }, { withCredentials: true });
                                const currentSubAnswersEn = checklistData["LOGO_USAGE"]?.sub_answers_en || [];
                                const newSubAnswersEn = [...currentSubAnswersEn];
                                newSubAnswersEn[0] = response.data.translated;
                                updateCriteria("LOGO_USAGE", "sub_answers_en", newSubAnswersEn);
                                toast.success("Çeviri tamamlandı");
                              } catch (error) { toast.error("Çeviri başarısız"); }
                              finally { setTranslatingItems(prev => ({ ...prev, ["LOGO_USAGE_sub_0"]: false })); }
                            }}
                            disabled={translatingItems["LOGO_USAGE_sub_0"] || !checklistData["LOGO_USAGE"]?.sub_answers?.[0]}
                          >
                            {translatingItems["LOGO_USAGE_sub_0"] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Languages className="w-3 h-3 mr-1" />EN</>}
                          </Button>
                          <span className="text-xs text-gray-400">İngilizceye çevir</span>
                        </div>
                        {checklistData["LOGO_USAGE"]?.sub_answers_en?.[0] && (
                          <Textarea
                            placeholder="English translation..."
                            value={checklistData["LOGO_USAGE"]?.sub_answers_en?.[0] || ""}
                            onChange={(e) => {
                              const currentSubAnswersEn = checklistData["LOGO_USAGE"]?.sub_answers_en || [];
                              const newSubAnswersEn = [...currentSubAnswersEn];
                              newSubAnswersEn[0] = e.target.value;
                              updateCriteria("LOGO_USAGE", "sub_answers_en", newSubAnswersEn);
                            }}
                            className="mt-2 h-14 text-sm border-blue-200 bg-blue-50/50"
                          />
                        )}
                      </div>
                      
                      {/* Question 2 - GSTC Logo */}
                      <div className="bg-white rounded-lg p-3 border border-amber-100">
                        <div className="flex items-start gap-2 mb-2">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">2</span>
                          <div className="flex-1">
                            <p className="text-sm text-gray-700">Kuruluş GSTC logosunu doğru kullanıyor mu?</p>
                            <p className="text-xs text-gray-500 italic mt-1">Does the organization use the GSTC logo correctly?</p>
                          </div>
                        </div>
                        <Textarea
                          placeholder="GSTC logo kullanımı hakkında gözlem yazın (TR)..."
                          value={checklistData["LOGO_USAGE"]?.sub_answers?.[1] || ""}
                          onChange={(e) => {
                            const currentSubAnswers = checklistData["LOGO_USAGE"]?.sub_answers || [];
                            const newSubAnswers = [...currentSubAnswers];
                            newSubAnswers[1] = e.target.value;
                            updateCriteria("LOGO_USAGE", "sub_answers", newSubAnswers);
                          }}
                          className="h-16 text-sm"
                        />
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={async () => {
                              const answer = checklistData["LOGO_USAGE"]?.sub_answers?.[1];
                              if (!answer?.trim()) { toast.error("Çevrilecek metin yok"); return; }
                              setTranslatingItems(prev => ({ ...prev, ["LOGO_USAGE_sub_1"]: true }));
                              try {
                                const response = await axios.post(`${API}/translate`, { text: answer }, { withCredentials: true });
                                const currentSubAnswersEn = checklistData["LOGO_USAGE"]?.sub_answers_en || [];
                                const newSubAnswersEn = [...currentSubAnswersEn];
                                newSubAnswersEn[1] = response.data.translated;
                                updateCriteria("LOGO_USAGE", "sub_answers_en", newSubAnswersEn);
                                toast.success("Çeviri tamamlandı");
                              } catch (error) { toast.error("Çeviri başarısız"); }
                              finally { setTranslatingItems(prev => ({ ...prev, ["LOGO_USAGE_sub_1"]: false })); }
                            }}
                            disabled={translatingItems["LOGO_USAGE_sub_1"] || !checklistData["LOGO_USAGE"]?.sub_answers?.[1]}
                          >
                            {translatingItems["LOGO_USAGE_sub_1"] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Languages className="w-3 h-3 mr-1" />EN</>}
                          </Button>
                          <span className="text-xs text-gray-400">İngilizceye çevir</span>
                        </div>
                        {checklistData["LOGO_USAGE"]?.sub_answers_en?.[1] && (
                          <Textarea
                            placeholder="English translation..."
                            value={checklistData["LOGO_USAGE"]?.sub_answers_en?.[1] || ""}
                            onChange={(e) => {
                              const currentSubAnswersEn = checklistData["LOGO_USAGE"]?.sub_answers_en || [];
                              const newSubAnswersEn = [...currentSubAnswersEn];
                              newSubAnswersEn[1] = e.target.value;
                              updateCriteria("LOGO_USAGE", "sub_answers_en", newSubAnswersEn);
                            }}
                            className="mt-2 h-14 text-sm border-blue-200 bg-blue-50/50"
                          />
                        )}
                      </div>
                      
                      {/* Question 3 - GSTC References and Claims */}
                      <div className="bg-white rounded-lg p-3 border border-amber-100">
                        <div className="flex items-start gap-2 mb-2">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">3</span>
                          <div className="flex-1">
                            <p className="text-sm text-gray-700">GSTC sertifikasyon program adı, tescilli ticari markalar ve logoların kullanımına ilişkin referansların ve üçüncü taraf iddialarının incelenmesi.</p>
                            <p className="text-xs text-gray-500 italic mt-1">Examination of references and third-party claims regarding the use of the GSTC certification program name, registered trademarks, and logos.</p>
                          </div>
                        </div>
                        <Textarea
                          placeholder="Referanslar ve iddialar hakkında gözlem yazın (TR)..."
                          value={checklistData["LOGO_USAGE"]?.sub_answers?.[2] || ""}
                          onChange={(e) => {
                            const currentSubAnswers = checklistData["LOGO_USAGE"]?.sub_answers || [];
                            const newSubAnswers = [...currentSubAnswers];
                            newSubAnswers[2] = e.target.value;
                            updateCriteria("LOGO_USAGE", "sub_answers", newSubAnswers);
                          }}
                          className="h-16 text-sm"
                        />
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={async () => {
                              const answer = checklistData["LOGO_USAGE"]?.sub_answers?.[2];
                              if (!answer?.trim()) { toast.error("Çevrilecek metin yok"); return; }
                              setTranslatingItems(prev => ({ ...prev, ["LOGO_USAGE_sub_2"]: true }));
                              try {
                                const response = await axios.post(`${API}/translate`, { text: answer }, { withCredentials: true });
                                const currentSubAnswersEn = checklistData["LOGO_USAGE"]?.sub_answers_en || [];
                                const newSubAnswersEn = [...currentSubAnswersEn];
                                newSubAnswersEn[2] = response.data.translated;
                                updateCriteria("LOGO_USAGE", "sub_answers_en", newSubAnswersEn);
                                toast.success("Çeviri tamamlandı");
                              } catch (error) { toast.error("Çeviri başarısız"); }
                              finally { setTranslatingItems(prev => ({ ...prev, ["LOGO_USAGE_sub_2"]: false })); }
                            }}
                            disabled={translatingItems["LOGO_USAGE_sub_2"] || !checklistData["LOGO_USAGE"]?.sub_answers?.[2]}
                          >
                            {translatingItems["LOGO_USAGE_sub_2"] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Languages className="w-3 h-3 mr-1" />EN</>}
                          </Button>
                          <span className="text-xs text-gray-400">İngilizceye çevir</span>
                        </div>
                        {checklistData["LOGO_USAGE"]?.sub_answers_en?.[2] && (
                          <Textarea
                            placeholder="English translation..."
                            value={checklistData["LOGO_USAGE"]?.sub_answers_en?.[2] || ""}
                            onChange={(e) => {
                              const currentSubAnswersEn = checklistData["LOGO_USAGE"]?.sub_answers_en || [];
                              const newSubAnswersEn = [...currentSubAnswersEn];
                              newSubAnswersEn[2] = e.target.value;
                              updateCriteria("LOGO_USAGE", "sub_answers_en", newSubAnswersEn);
                            }}
                            className="mt-2 h-14 text-sm border-blue-200 bg-blue-50/50"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Main Evaluation for Logo Usage */}
                  <div className="mb-4 border-l-4 border-purple-200 pl-4 bg-purple-50/30 rounded-r-lg py-3">
                    <Label className="text-xs text-purple-700 font-semibold mb-2">
                      Ana Madde Değerlendirmesi (Özet) / Main Criteria Evaluation (Summary)
                    </Label>
                    <Textarea
                      placeholder="Logo kullanımı genel değerlendirme (TR)..."
                      value={checklistData["LOGO_USAGE"]?.main_evaluation || ""}
                      onChange={(e) => updateCriteria("LOGO_USAGE", "main_evaluation", e.target.value)}
                      className="mt-1 h-16 border-purple-200"
                    />
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={async () => {
                          const text = checklistData["LOGO_USAGE"]?.main_evaluation;
                          if (!text?.trim()) { toast.error("Çevrilecek metin yok"); return; }
                          setTranslatingItems(prev => ({ ...prev, ["LOGO_USAGE_main"]: true }));
                          try {
                            const response = await axios.post(`${API}/translate`, { text }, { withCredentials: true });
                            updateCriteria("LOGO_USAGE", "main_evaluation_en", response.data.translated);
                            toast.success("Çeviri tamamlandı");
                          } catch (error) { toast.error("Çeviri başarısız"); }
                          finally { setTranslatingItems(prev => ({ ...prev, ["LOGO_USAGE_main"]: false })); }
                        }}
                        disabled={translatingItems["LOGO_USAGE_main"] || !checklistData["LOGO_USAGE"]?.main_evaluation}
                      >
                        {translatingItems["LOGO_USAGE_main"] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Languages className="w-3 h-3 mr-1" />EN</>}
                      </Button>
                      <span className="text-xs text-gray-400">İngilizceye çevir</span>
                    </div>
                    {checklistData["LOGO_USAGE"]?.main_evaluation_en && (
                      <Textarea
                        placeholder="English translation..."
                        value={checklistData["LOGO_USAGE"]?.main_evaluation_en || ""}
                        onChange={(e) => updateCriteria("LOGO_USAGE", "main_evaluation_en", e.target.value)}
                        className="mt-2 h-14 text-sm border-blue-200 bg-blue-50/50"
                      />
                    )}
                  </div>
                  
                  {/* NC Notes for Logo Usage - only shown for nonconformities */}
                  {(checklistData["LOGO_USAGE"]?.status === 'major_nc' || checklistData["LOGO_USAGE"]?.status === 'minor_nc') && (
                    <div className="border-2 border-orange-200 rounded-lg p-3 bg-orange-50/50">
                      <Label className="text-xs text-orange-700 font-semibold flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" />
                        Uygunsuzluk Açıklaması (Zorunlu) / NC Description (Required)
                      </Label>
                      <Textarea
                        placeholder="Logo kullanımındaki uygunsuzluğu detaylı açıklayın..."
                        value={checklistData["LOGO_USAGE"]?.notes || ""}
                        onChange={(e) => updateCriteria("LOGO_USAGE", "notes", e.target.value)}
                        className="mt-1 h-20 border-orange-300"
                      />
                      <div className="flex items-center gap-2 mt-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleTranslateNotes("LOGO_USAGE")}
                          disabled={translatingItems["LOGO_USAGE_notes"] || !checklistData["LOGO_USAGE"]?.notes}
                        >
                          {translatingItems["LOGO_USAGE_notes"] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Languages className="w-3 h-3 mr-1" />}
                          İngilizceye Çevir
                        </Button>
                      </div>
                      {checklistData["LOGO_USAGE"]?.notes_en && (
                        <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                          <Label className="text-xs text-blue-600 font-medium">NC Description (English)</Label>
                          <Textarea
                            value={checklistData["LOGO_USAGE"]?.notes_en || ""}
                            onChange={(e) => updateCriteria("LOGO_USAGE", "notes_en", e.target.value)}
                            className="mt-1 h-16 text-sm bg-white"
                            placeholder="English translation..."
                          />
                        </div>
                      )}
                    </div>
                  )}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* REPORT TAB - Hidden for Planning Expert */}
          {canViewFullReport && (
          <TabsContent value="report">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{isStage2 ? 'FR.04 Denetim Raporu' : 'FR.10 Audit Report - Hotel/Accommodation Services'}</CardTitle>
                  <CardDescription>Denetim raporu bilgilerini doldurun</CardDescription>
                </div>
                <div className="flex gap-2">
                  {/* Translate button only for Stage 3 */}
                  {isStage3 && (
                    <Button 
                      variant="outline" 
                      onClick={handleTranslateAllSections} 
                      disabled={translatingAll}
                      className="text-blue-600 border-blue-300"
                    >
                      {translatingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Languages className="w-4 h-4 mr-2" />}
                      Tüm Bölümleri Çevir
                    </Button>
                  )}
                  <Button onClick={saveReport} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Kaydet
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-8">
                
                {/* Section 1: Client Information */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-4 flex items-center gap-2 text-lg border-b pb-2">
                    <Building2 className="w-5 h-5 text-[#1A4D2E]" />
                    1. CLIENT INFORMATION / MÜŞTERİ BİLGİLERİ
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {template?.report?.header_fields?.map((field) => (
                      <div key={field.id} className={field.type === "textarea" ? "md:col-span-2" : ""}>
                        <Label className="text-sm text-gray-600">{field.label}</Label>
                        {field.type === "textarea" ? (
                          <Textarea
                            value={headerData[field.id] || field.default || ""}
                            onChange={(e) => setHeaderData({...headerData, [field.id]: e.target.value})}
                            className="mt-1"
                            rows={2}
                          />
                        ) : field.type === "select" ? (
                          <Select
                            value={headerData[field.id] || ""}
                            onValueChange={(v) => setHeaderData({...headerData, [field.id]: v})}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Seçin" />
                            </SelectTrigger>
                            <SelectContent>
                              {field.options?.map((opt) => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : field.type === "date" ? (
                          <Input
                            type="date"
                            value={formatDateForInput(headerData[field.id]) || ""}
                            onChange={(e) => {
                              setHeaderData({...headerData, [field.id]: e.target.value});
                              setHasUnsavedChanges(true);
                            }}
                            className="mt-1"
                          />
                        ) : (
                          <Input
                            type={field.type === "number" ? "number" : "text"}
                            value={headerData[field.id] || field.default || ""}
                            onChange={(e) => setHeaderData({...headerData, [field.id]: e.target.value})}
                            className="mt-1"
                          />
                        )}
                        {field.auto_fill && headerData[field.id] && (
                          <p className="text-xs text-green-600 mt-1">✓ Otomatik dolduruldu</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Report Sections 2-12 */}
                {template?.report?.report_sections?.map((section) => (
                  <div key={section.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4 border-b pb-2">
                      <h3 className="font-semibold flex items-center gap-2 text-lg">
                        <FileText className="w-5 h-5 text-[#1A4D2E]" />
                        {section.number}. {section.title}
                      </h3>
                    </div>
                    {section.description && (
                      <p className="text-sm text-gray-500 mb-4 italic">{section.description}</p>
                    )}
                    <div className="space-y-4">
                      {section.fields?.map((field) => (
                        <div key={field.id}>
                          <Label className="text-sm font-medium">{field.label}</Label>
                          {field.type === "textarea" ? (
                            <>
                              <Textarea
                                placeholder="Türkçe metin..."
                                value={reportSections[field.id] || ""}
                                onChange={(e) => setReportSections({...reportSections, [field.id]: e.target.value})}
                                className="mt-1"
                                rows={field.rows || 4}
                              />
                              {/* Translate button - same style as checklist */}
                              <div className="flex items-center gap-2 mt-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleTranslateSection(field.id)}
                                  disabled={translatingItems[`section_${field.id}`] || !reportSections[field.id]}
                                >
                                  {translatingItems[`section_${field.id}`] ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <Languages className="w-3 h-3 mr-1" />
                                  )}
                                  İngilizceye Çevir
                                </Button>
                              </div>
                              {/* English translation field - same style as checklist */}
                              {reportSections[`${field.id}_en`] && (
                                <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                                  <Label className="text-xs text-blue-600 font-medium">English Translation</Label>
                                  <Textarea
                                    value={reportSections[`${field.id}_en`] || ""}
                                    onChange={(e) => setReportSections({...reportSections, [`${field.id}_en`]: e.target.value})}
                                    className="mt-1 text-sm bg-white"
                                    rows={field.rows || 4}
                                    placeholder="English translation..."
                                  />
                                </div>
                              )}
                            </>
                          ) : field.type === "select" ? (
                            <Select
                              value={reportSections[field.id] || ""}
                              onValueChange={(v) => setReportSections({...reportSections, [field.id]: v})}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Seçin" />
                              </SelectTrigger>
                              <SelectContent>
                                {field.options?.map((opt) => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              value={reportSections[field.id] || ""}
                              onChange={(e) => setReportSections({...reportSections, [field.id]: e.target.value})}
                              className="mt-1"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* NC Summary for section that needs it */}
                    {section.id === "section_11" && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm font-medium mb-2">Checklist Özeti (Otomatik):</p>
                        <div className="flex gap-4 flex-wrap">
                          <Badge className="bg-green-100 text-green-800">Conform: {summary.conform}</Badge>
                          <Badge className="bg-red-100 text-red-800">Major NC: {summary.major_nc}</Badge>
                          <Badge className="bg-orange-100 text-orange-800">Minor NC: {summary.minor_nc}</Badge>
                          <Badge className="bg-blue-100 text-blue-800">Observation: {summary.observation}</Badge>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Team Leader Signature - Hidden completely */}

              </CardContent>
            </Card>
          </TabsContent>
          )}

                {/* Interview Records - Hidden for Planning Expert */}
                {canViewFullReport && (
                <TabsContent value="interviews">
                <Card>
                  <CardHeader>
                    <CardTitle>Mülakat Kayıtları</CardTitle>
                    <CardDescription>Denetim sürecinde görüşülen kişiler</CardDescription>
                  </CardHeader>
                  <CardContent>
                  {interviewRecords.length === 0 ? (
                    <div className="text-center py-6 bg-gray-50 rounded-lg">
                      <p className="text-gray-500 mb-2">Henüz mülakat kaydı eklenmedi</p>
                      <Button size="sm" variant="outline" onClick={addInterview}>
                        <Plus className="w-4 h-4 mr-2" />
                        Mülakat Ekle
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {interviewRecords.map((interview, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-start bg-gray-50 p-3 rounded-lg">
                          <div className="col-span-3">
                            <Label className="text-xs">Ad Soyad</Label>
                            <Input
                              value={interview.name || ""}
                              onChange={(e) => updateInterview(idx, "name", e.target.value)}
                              placeholder="İsim"
                              className="mt-1"
                            />
                          </div>
                          <div className="col-span-3">
                            <Label className="text-xs">Pozisyon</Label>
                            <Input
                              value={interview.position || ""}
                              onChange={(e) => updateInterview(idx, "position", e.target.value)}
                              placeholder="Unvan"
                              className="mt-1"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">Departman</Label>
                            <Input
                              value={interview.department || ""}
                              onChange={(e) => updateInterview(idx, "department", e.target.value)}
                              placeholder="Birim"
                              className="mt-1"
                            />
                          </div>
                          <div className="col-span-3">
                            <Label className="text-xs">Notlar</Label>
                            <Input
                              value={interview.notes || ""}
                              onChange={(e) => updateInterview(idx, "notes", e.target.value)}
                              placeholder="Görüşme notları"
                              className="mt-1"
                            />
                          </div>
                          <div className="col-span-1 flex items-end justify-center pb-1">
                            <Button size="sm" variant="ghost" onClick={() => removeInterview(idx)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      <Button size="sm" variant="outline" onClick={addInterview}>
                        <Plus className="w-4 h-4 mr-2" />
                        Mülakat Ekle
                      </Button>
                    </div>
                  )}
                  </CardContent>
                </Card>
                </TabsContent>
                )}

                {/* Meeting Attendees Tab - Hidden for Planning Expert */}
                {canViewFullReport && (
                <TabsContent value="meetings">
                {/* Meeting Details Card */}
                <Card className="mb-6">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        Toplantı Bilgileri (FR.45)
                      </CardTitle>
                      <CardDescription>Açılış ve kapanış toplantısı tarih/saat bilgilerini girin</CardDescription>
                    </div>
                    <Button onClick={saveReport} disabled={saving} data-testid="save-meeting-btn">
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Kaydet
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Opening Meeting */}
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <h4 className="font-semibold text-green-800 mb-3">Açılış Toplantısı</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="opening-date" className="text-sm">Tarih</Label>
                            <Input
                              id="opening-date"
                              type="date"
                              value={formatDateForInput(meetingDetails.openingDate) || ""}
                              onChange={(e) => {
                                setMeetingDetails({...meetingDetails, openingDate: e.target.value});
                                setHasUnsavedChanges(true);
                              }}
                              data-testid="opening-date-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="opening-hour" className="text-sm">Saat</Label>
                            <Input
                              id="opening-hour"
                              type="time"
                              value={meetingDetails.openingHour || "09:00"}
                              onChange={(e) => setMeetingDetails({...meetingDetails, openingHour: e.target.value})}
                              data-testid="opening-hour-input"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Closing Meeting */}
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <h4 className="font-semibold text-blue-800 mb-3">Kapanış Toplantısı</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="closing-date" className="text-sm">Tarih</Label>
                            <Input
                              id="closing-date"
                              type="date"
                              value={formatDateForInput(meetingDetails.closingDate) || ""}
                              onChange={(e) => {
                                setMeetingDetails({...meetingDetails, closingDate: e.target.value});
                                setHasUnsavedChanges(true);
                              }}
                              data-testid="closing-date-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="closing-hour" className="text-sm">Saat</Label>
                            <Input
                              id="closing-hour"
                              type="time"
                              value={meetingDetails.closingHour || "17:00"}
                              onChange={(e) => setMeetingDetails({...meetingDetails, closingHour: e.target.value})}
                              data-testid="closing-hour-input"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Opening Meeting Agenda Card */}
                <Card className="mb-6">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-green-800">
                          <CheckSquare className="w-5 h-5" />
                          Açılış Toplantısı Gündemi
                        </CardTitle>
                        <CardDescription>Görüşülen gündem maddelerini işaretleyin</CardDescription>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          const allChecked = OPENING_AGENDA_ITEMS.every(item => openingAgendaChecked[item.id]);
                          const newState = {};
                          OPENING_AGENDA_ITEMS.forEach(item => {
                            newState[item.id] = !allChecked;
                          });
                          setOpeningAgendaChecked(newState);
                        }}
                        data-testid="select-all-opening-agenda"
                      >
                        <CheckSquare className="w-4 h-4 mr-2" />
                        {OPENING_AGENDA_ITEMS.every(item => openingAgendaChecked[item.id]) ? "Tümünü Kaldır" : "Tümünü Seç"}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {OPENING_AGENDA_ITEMS.map((item) => (
                        <div 
                          key={item.id} 
                          className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                            openingAgendaChecked[item.id] ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <Checkbox
                            id={`opening-agenda-${item.id}`}
                            checked={openingAgendaChecked[item.id] || false}
                            onCheckedChange={(checked) => {
                              setOpeningAgendaChecked({...openingAgendaChecked, [item.id]: checked});
                            }}
                            data-testid={`opening-agenda-checkbox-${item.id}`}
                          />
                          <label 
                            htmlFor={`opening-agenda-${item.id}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            <span className="font-medium text-gray-700">{item.id}.</span>{" "}
                            {item.text}
                          </label>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Closing Meeting Agenda Card */}
                <Card className="mb-6">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-blue-800">
                          <CheckSquare className="w-5 h-5" />
                          Kapanış Toplantısı Gündemi
                        </CardTitle>
                        <CardDescription>Görüşülen gündem maddelerini işaretleyin</CardDescription>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          const allChecked = CLOSING_AGENDA_ITEMS.every(item => closingAgendaChecked[item.id]);
                          const newState = {};
                          CLOSING_AGENDA_ITEMS.forEach(item => {
                            newState[item.id] = !allChecked;
                          });
                          setClosingAgendaChecked(newState);
                        }}
                        data-testid="select-all-closing-agenda"
                      >
                        <CheckSquare className="w-4 h-4 mr-2" />
                        {CLOSING_AGENDA_ITEMS.every(item => closingAgendaChecked[item.id]) ? "Tümünü Kaldır" : "Tümünü Seç"}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {CLOSING_AGENDA_ITEMS.map((item) => (
                        <div 
                          key={item.id} 
                          className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                            closingAgendaChecked[item.id] ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <Checkbox
                            id={`closing-agenda-${item.id}`}
                            checked={closingAgendaChecked[item.id] || false}
                            onCheckedChange={(checked) => {
                              setClosingAgendaChecked({...closingAgendaChecked, [item.id]: checked});
                            }}
                            data-testid={`closing-agenda-checkbox-${item.id}`}
                          />
                          <label 
                            htmlFor={`closing-agenda-${item.id}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            <span className="font-medium text-gray-700">{item.id}.</span>{" "}
                            {item.text}
                          </label>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Participants Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Toplantı Katılımcıları
                    </CardTitle>
                    <CardDescription>Açılış ve kapanış toplantısına katılan kişiler</CardDescription>
                  </CardHeader>
                  <CardContent>
                  {meetingAttendees.length === 0 ? (
                    <div className="text-center py-6 bg-gray-50 rounded-lg">
                      <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-gray-500 mb-2">Henüz katılımcı eklenmedi</p>
                      <Button size="sm" variant="outline" onClick={addAttendee} data-testid="add-first-attendee-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Katılımcı Ekle
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-10 gap-2 text-xs font-medium text-gray-500 px-3">
                        <div className="col-span-3">Ad</div>
                        <div className="col-span-3">Soyad</div>
                        <div className="col-span-3">Unvan</div>
                        <div className="col-span-1"></div>
                      </div>
                      {meetingAttendees.map((attendee, idx) => (
                        <div key={idx} className="grid grid-cols-10 gap-2 items-center bg-gray-50 p-3 rounded-lg" data-testid={`attendee-row-${idx}`}>
                          <div className="col-span-3">
                            <Input
                              value={attendee.name || ""}
                              onChange={(e) => updateAttendee(idx, "name", e.target.value)}
                              placeholder="Ad"
                              data-testid={`attendee-name-${idx}`}
                            />
                          </div>
                          <div className="col-span-3">
                            <Input
                              value={attendee.surname || ""}
                              onChange={(e) => updateAttendee(idx, "surname", e.target.value)}
                              placeholder="Soyad"
                              data-testid={`attendee-surname-${idx}`}
                            />
                          </div>
                          <div className="col-span-3">
                            <Input
                              value={attendee.title || ""}
                              onChange={(e) => updateAttendee(idx, "title", e.target.value)}
                              placeholder="Unvan"
                              data-testid={`attendee-title-${idx}`}
                            />
                          </div>
                          <div className="col-span-1 flex justify-center">
                            <Button size="sm" variant="ghost" onClick={() => removeAttendee(idx)} data-testid={`remove-attendee-${idx}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between items-center pt-4 border-t">
                        <Button size="sm" variant="outline" onClick={addAttendee} data-testid="add-attendee-btn">
                          <Plus className="w-4 h-4 mr-2" />
                          Katılımcı Ekle
                        </Button>
                        {/* Word/PDF buttons only for Stage 3 - Stage 2 has these in Documents section */}
                        {!isStage2 && (
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={handleDownloadFR45}
                            data-testid="download-fr45-btn"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Word
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={handleDownloadFR45PDF}
                            disabled={downloadingDoc === "FR.45_pdf"}
                            data-testid="download-fr45-pdf-btn"
                          >
                            {downloadingDoc === "FR.45_pdf" ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <FileDown className="w-4 h-4 mr-1" />
                                PDF
                              </>
                            )}
                          </Button>
                        </div>
                        )}
                      </div>
                    </div>
                  )}
                  </CardContent>
                </Card>
                </TabsContent>
                )}

          {/* EVIDENCE TAB - Visible for all including Planning Expert */}
          <TabsContent value="evidence">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Photos Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Camera className="w-5 h-5" />
                    Saha Fotoğrafları
                  </CardTitle>
                  <CardDescription>Max 25MB/dosya - JPEG, PNG, WebP</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Upload Button */}
                    <div className="border-2 border-dashed rounded-lg p-6 text-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handlePhotoUpload(e)}
                        className="hidden"
                        id="photo-upload"
                      />
                      <label htmlFor="photo-upload" className="cursor-pointer">
                        {uploadingPhoto ? (
                          <Loader2 className="w-8 h-8 mx-auto animate-spin text-gray-400" />
                        ) : (
                          <Upload className="w-8 h-8 mx-auto text-gray-400" />
                        )}
                        <p className="mt-2 text-sm text-gray-600">
                          Fotoğraf yüklemek için tıklayın
                        </p>
                      </label>
                    </div>

                    {/* Photo List */}
                    {photos.length > 0 && (
                      <div className="grid grid-cols-2 gap-3">
                        {photos.map((photo, idx) => {
                          // Build proper image src with data URI prefix
                          const getImageSrc = (photoData, contentType, filename) => {
                            if (!photoData) return null;
                            if (photoData.startsWith('data:')) return photoData;
                            // Determine mime type from content_type or filename
                            const mimeType = contentType || 
                              (filename?.endsWith('.png') ? 'image/png' : 'image/jpeg');
                            return `data:${mimeType};base64,${photoData}`;
                          };
                          const imageSrc = getImageSrc(photo.data, photo.content_type, photo.filename);
                          
                          return (
                            <div key={photo.photo_id || idx} className="border rounded-lg p-2 relative group">
                              <div 
                                className="aspect-video bg-gray-100 rounded flex items-center justify-center cursor-pointer hover:bg-gray-200 overflow-hidden"
                                onClick={() => handleViewPhoto(photo)}
                              >
                                {imageSrc ? (
                                  <img 
                                    src={imageSrc} 
                                    alt={photo.filename} 
                                    className="w-full h-full object-cover rounded"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div className={`flex flex-col items-center ${imageSrc ? 'hidden' : 'flex'}`}>
                                  <Image className="w-8 h-8 text-gray-400" />
                                  <span className="text-xs text-gray-400 mt-1">Görüntüle</span>
                                </div>
                              </div>
                              <p className="text-xs text-gray-600 mt-1 truncate">{photo.filename}</p>
                              {photo.criteria_id && (
                                <Badge variant="outline" className="mt-1 text-xs">{photo.criteria_id}</Badge>
                              )}
                              {/* Delete button */}
                              <Button
                                variant="destructive"
                                size="icon"
                                className="absolute top-1 right-1 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeletePhoto(photo.photo_id);
                                }}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Links Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Link className="w-5 h-5" />
                    Kanıt Linkleri
                  </CardTitle>
                  <CardDescription>WeTransfer veya Google Drive linkleri</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Add Link Form */}
                    <div className="border rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Link Türü</Label>
                          <Select
                            value={newLink.link_type}
                            onValueChange={(v) => setNewLink({...newLink, link_type: v})}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="wetransfer">WeTransfer</SelectItem>
                              <SelectItem value="google_drive">Google Drive</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">İlgili Kriterler</Label>
                          <Input
                            placeholder="A1, B2, C3..."
                            value={newLink.criteria_ids.join(", ")}
                            onChange={(e) => setNewLink({
                              ...newLink, 
                              criteria_ids: e.target.value.split(",").map(s => s.trim()).filter(Boolean)
                            })}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">URL</Label>
                        <Input
                          placeholder="https://..."
                          value={newLink.url}
                          onChange={(e) => setNewLink({...newLink, url: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Açıklama</Label>
                        <Input
                          placeholder="Link içeriği hakkında..."
                          value={newLink.description}
                          onChange={(e) => setNewLink({...newLink, description: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <Button onClick={addEvidenceLink} className="w-full" size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        Link Ekle
                      </Button>
                    </div>

                    {/* Link List */}
                    {evidenceLinks.length > 0 && (
                      <div className="space-y-2">
                        {evidenceLinks.map((link, idx) => (
                          <div key={link.link_id || idx} className="border rounded-lg p-3 flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">
                                  {link.link_type === "wetransfer" ? "WeTransfer" : "Google Drive"}
                                </Badge>
                                {link.criteria_ids?.map(c => (
                                  <Badge key={c} className="text-xs" style={{ backgroundColor: primaryColor, color: 'white' }}>
                                    {c}
                                  </Badge>
                                ))}
                              </div>
                              <p className="text-sm text-gray-600 mt-1 truncate">{link.description || link.url}</p>
                            </div>
                            <a href={link.url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* NCR (NONCONFORMITY) TAB - Hidden for Planning Expert */}
          {canViewFullReport && (
          <TabsContent value="ncr">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <AlertOctagon className="w-5 h-5 text-red-600" />
                    Uygunsuzluk Bildirim Formu (FR.37)
                  </CardTitle>
                  <CardDescription>
                    Checklist'te tespit edilen uygunsuzluklar otomatik olarak listelenir
                  </CardDescription>
                </div>
                <Button onClick={saveNcrData} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Kaydet
                </Button>
              </CardHeader>
              <CardContent>
                {getNonconformities().length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900">Uygunsuzluk Yok</h3>
                    <p className="text-gray-500 mt-2">
                      Checklist'te Major NC veya Minor NC olarak işaretlenen kriter bulunmuyor.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Instructions */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-amber-800">Önemli Bilgi</p>
                          <ul className="mt-2 text-amber-700 space-y-1">
                            <li>• Düzeltici faaliyetler <strong>14 gün</strong> içinde denetçiye iletilmelidir.</li>
                            <li>• Kapatma kanıtları <strong>90 gün (3 ay)</strong> içinde sunulmalıdır.</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="flex gap-4">
                      <div className="flex items-center gap-2 px-4 py-2 bg-red-50 rounded-lg">
                        <span className="w-3 h-3 rounded-full bg-red-500"></span>
                        <span className="text-sm font-medium text-red-800">
                          {getNonconformities().filter(nc => nc.classification === 'major_nc').length} Major NC
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-lg">
                        <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                        <span className="text-sm font-medium text-orange-800">
                          {getNonconformities().filter(nc => nc.classification === 'minor_nc').length} Minor NC
                        </span>
                      </div>
                    </div>

                    {/* NCR List */}
                    <div className="space-y-4">
                      {getNonconformities().map((nc, index) => (
                        <div 
                          key={nc.criteria_id} 
                          className={`border rounded-lg overflow-hidden ${
                            nc.classification === 'major_nc' ? 'border-red-300' : 'border-orange-300'
                          }`}
                        >
                          {/* NC Header - Auditor Section */}
                          <div className={`p-4 ${
                            nc.classification === 'major_nc' ? 'bg-red-50' : 'bg-orange-50'
                          }`}>
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <span className={`px-2 py-1 text-xs font-bold rounded ${
                                  nc.classification === 'major_nc' 
                                    ? 'bg-red-500 text-white' 
                                    : 'bg-orange-500 text-white'
                                }`}>
                                  #{index + 1}
                                </span>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <Badge style={{ backgroundColor: primaryColor, color: 'white' }}>
                                      {nc.criteria_id}
                                    </Badge>
                                    <span className={`px-2 py-0.5 text-xs rounded ${
                                      nc.classification === 'major_nc'
                                        ? 'bg-red-200 text-red-800'
                                        : 'bg-orange-200 text-orange-800'
                                    }`}>
                                      {nc.classification === 'major_nc' ? 'Major NC' : 'Minor NC'}
                                    </span>
                                  </div>
                                  <p className="font-medium text-gray-900 mt-1">{nc.criteria_title}</p>
                                  <p className="text-xs text-gray-500 mt-0.5">{nc.section_title}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {ncrData[nc.criteria_id]?.status === 'closed' ? (
                                  <Badge className="bg-green-100 text-green-800">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Kapatıldı
                                  </Badge>
                                ) : ncrData[nc.criteria_id]?.root_cause ? (
                                  <Badge className="bg-blue-100 text-blue-800">
                                    <Clock className="w-3 h-3 mr-1" />
                                    Yanıt Bekliyor
                                  </Badge>
                                ) : (
                                  <Badge className="bg-gray-100 text-gray-800">
                                    <AlertTriangle className="w-3 h-3 mr-1" />
                                    Açık
                                  </Badge>
                                )}
                              </div>
                            </div>
                            
                            {/* Auditor filled info */}
                            <div className="mt-4">
                              <div>
                                <Label className="text-xs text-gray-600">Uygunsuzluk Açıklaması</Label>
                                <p className="mt-1 text-sm bg-white p-2 rounded border min-h-[60px]">
                                  {nc.description || <span className="text-gray-400 italic">Açıklama girilmemiş</span>}
                                </p>
                              </div>
                            </div>

                            {/* Closure info */}
                            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <Label className="text-xs text-gray-600">Sorumlu Kişi</Label>
                                <Input
                                  placeholder="İsim"
                                  value={ncrData[nc.criteria_id]?.responsible_person || headerData.management_rep || ''}
                                  onChange={(e) => updateNcrResponse(nc.criteria_id, 'responsible_person', e.target.value)}
                                  className="mt-1 bg-white"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-gray-600">Kapatma Tarihi</Label>
                                <Input
                                  type="date"
                                  value={ncrData[nc.criteria_id]?.closure_date || (() => {
                                    // Calculate 3 months after audit date - handle DD.MM.YYYY format
                                    const auditDateStr = headerData.audit_date_start || headerData.audit_date?.split(' - ')[0] || '';
                                    if (auditDateStr) {
                                      const parts = auditDateStr.split('.');
                                      if (parts.length === 3) {
                                        const [day, month, year] = parts;
                                        // Create date in local timezone (Turkey), add 3 months
                                        const closureDate = new Date(parseInt(year), parseInt(month) - 1 + 3, parseInt(day));
                                        // Format as YYYY-MM-DD for input type="date"
                                        const y = closureDate.getFullYear();
                                        const m = String(closureDate.getMonth() + 1).padStart(2, '0');
                                        const d = String(closureDate.getDate()).padStart(2, '0');
                                        return `${y}-${m}-${d}`;
                                      }
                                    }
                                    return '';
                                  })()}
                                  onChange={(e) => updateNcrResponse(nc.criteria_id, 'closure_date', e.target.value)}
                                  className="mt-1 bg-white"
                                />
                              </div>
                              <div className="md:col-span-2">
                                <Label className="text-xs text-gray-600">Durum</Label>
                                <Select
                                  value={ncrData[nc.criteria_id]?.status || 'open'}
                                  onValueChange={(v) => updateNcrResponse(nc.criteria_id, 'status', v)}
                                >
                                  <SelectTrigger className="mt-1 bg-white">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="open">Açık</SelectItem>
                                    <SelectItem value="in_progress">İşlemde</SelectItem>
                                    <SelectItem value="pending_verification">Doğrulama Bekliyor</SelectItem>
                                    <SelectItem value="closed">Kapatıldı</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>

                          {/* Client Response Section - Removed: Customer fills this on the printed form */}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* DOCUMENTS TAB - Accessible to all including Planning Expert */}
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="w-5 h-5" />
                  Denetim Belgeleri
                </CardTitle>
                <CardDescription>
                  {isStage2 ? '2. Aşama (TR-I) formlarını indirin' : '3. Aşama (GSTC) formlarını indirin'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Stage 3 Documents Section - Only show for Stage 3 */}
                {isStage3 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2 border-b pb-2">
                    <span className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">3</span>
                    3. Aşama Denetim Formları
                  </h3>
                  <div className="space-y-3">
                    {/* FR.08 Auditor Designation */}
                    <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">FR.08 Denetçi Görevlendirme</p>
                            <p className="text-xs text-gray-500">Auditor Designation Form</p>
                            {signedPdfStatus["FR.08"]?.uploaded && (
                              <p className="text-xs text-green-600 mt-1">✓ İmzalı PDF yüklendi</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDocument("FR.08")}
                            disabled={downloadingDoc === "FR.08"}
                          >
                            {downloadingDoc === "FR.08" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" />Word</>}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => downloadPDF("FR.08")}
                            disabled={downloadingDoc === "FR.08_pdf"}
                          >
                            {downloadingDoc === "FR.08_pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileDown className="w-4 h-4 mr-1" />PDF</>}
                          </Button>
                          {renderSignedPdfButtons("FR.08")}
                        </div>
                      </div>
                    </div>

                    {/* FR.09 Audit Plan */}
                    <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                            <ClipboardList className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">FR.09 Denetim Planı</p>
                            <p className="text-xs text-gray-500">Audit Plan for Hotels & Accommodations</p>
                            {signedPdfStatus["FR.09"]?.uploaded && (
                              <p className="text-xs text-green-600 mt-1">✓ İmzalı PDF yüklendi</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDocument("FR.09")}
                            disabled={downloadingDoc === "FR.09"}
                          >
                            {downloadingDoc === "FR.09" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" />Word</>}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => downloadPDF("FR.09")}
                            disabled={downloadingDoc === "FR.09_pdf"}
                          >
                            {downloadingDoc === "FR.09_pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileDown className="w-4 h-4 mr-1" />PDF</>}
                          </Button>
                          {renderSignedPdfButtons("FR.09")}
                        </div>
                      </div>
                    </div>

                    {/* FR.13 Audit Notification */}
                    <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                            <Send className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">FR.13 Denetim Bildirimi</p>
                            <p className="text-xs text-gray-500">Audit Notification and Confirmation</p>
                            {signedPdfStatus["FR.13"]?.uploaded && (
                              <p className="text-xs text-green-600 mt-1">✓ İmzalı PDF yüklendi</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDocument("FR.13")}
                            disabled={downloadingDoc === "FR.13"}
                          >
                            {downloadingDoc === "FR.13" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" />Word</>}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => downloadPDF("FR.13")}
                            disabled={downloadingDoc === "FR.13_pdf"}
                          >
                            {downloadingDoc === "FR.13_pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileDown className="w-4 h-4 mr-1" />PDF</>}
                          </Button>
                          {renderSignedPdfButtons("FR.13")}
                        </div>
                      </div>
                    </div>

                    {/* FR.45 Meeting Minutes */}
                    <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                            <FileCheck className="w-5 h-5 text-teal-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">FR.45 Toplantı Tutanağı</p>
                            <p className="text-xs text-gray-500">Opening/Closing Meeting Minutes</p>
                            {signedPdfStatus["FR.45"]?.uploaded && (
                              <p className="text-xs text-green-600 mt-1">✓ İmzalı PDF yüklendi</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDocument("FR.45")}
                            disabled={downloadingDoc === "FR.45"}
                            data-testid="download-fr45-word"
                          >
                            {downloadingDoc === "FR.45" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" />Word</>}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={handleDownloadFR45PDF}
                            disabled={downloadingDoc === "FR.45_pdf"}
                            data-testid="download-fr45-pdf"
                          >
                            {downloadingDoc === "FR.45_pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileDown className="w-4 h-4 mr-1" />PDF</>}
                          </Button>
                          {renderSignedPdfButtons("FR.45")}
                        </div>
                      </div>
                    </div>

                    {/* FR.50 Checklist */}
                    <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                            <ClipboardList className="w-5 h-5 text-red-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">FR.50 Audit Checklist</p>
                            <p className="text-xs text-gray-500">GSTC Criteria Checklist</p>
                            {signedPdfStatus["FR.50"]?.uploaded && (
                              <p className="text-xs text-green-600 mt-1">✓ İmzalı PDF yüklendi</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDocument("FR.50")}
                            disabled={downloadingDoc === "FR.50"}
                            data-testid="download-fr50-word"
                          >
                            {downloadingDoc === "FR.50" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" />Word</>}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => downloadPDF("FR.50")}
                            disabled={downloadingDoc === "FR.50_pdf"}
                            data-testid="download-fr50-pdf"
                          >
                            {downloadingDoc === "FR.50_pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileDown className="w-4 h-4 mr-1" />PDF</>}
                          </Button>
                          {renderSignedPdfButtons("FR.50")}
                        </div>
                      </div>
                    </div>

                    {/* FR.10 Audit Report */}
                    <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">FR.10 Denetim Raporu</p>
                            <p className="text-xs text-gray-500">Audit Report</p>
                            {signedPdfStatus["FR.10"]?.uploaded && (
                              <p className="text-xs text-green-600 mt-1">✓ İmzalı PDF yüklendi</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDocument("FR.10")}
                            disabled={downloadingDoc === "FR.10"}
                            data-testid="download-fr10-word"
                          >
                            {downloadingDoc === "FR.10" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" />Word</>}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => downloadPDF("FR.10")}
                            disabled={downloadingDoc === "FR.10_pdf"}
                            data-testid="download-fr10-pdf"
                          >
                            {downloadingDoc === "FR.10_pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileDown className="w-4 h-4 mr-1" />PDF</>}
                          </Button>
                          {renderSignedPdfButtons("FR.10")}
                        </div>
                      </div>
                    </div>

                    {/* FR.37 NCR Form */}
                    <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                            <AlertOctagon className="w-5 h-5 text-yellow-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">FR.37 Uygunsuzluk Bildirimi</p>
                            <p className="text-xs text-gray-500">Nonconformity Notification Form</p>
                            {signedPdfStatus["FR.37"]?.uploaded && (
                              <p className="text-xs text-green-600 mt-1">✓ İmzalı PDF yüklendi</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDocument("FR.37")}
                            disabled={downloadingDoc === "FR.37"}
                            data-testid="download-fr37-word"
                          >
                            {downloadingDoc === "FR.37" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" />Word</>}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => downloadPDF("FR.37")}
                            disabled={downloadingDoc === "FR.37_pdf"}
                            data-testid="download-fr37-pdf"
                          >
                            {downloadingDoc === "FR.37_pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileDown className="w-4 h-4 mr-1" />PDF</>}
                          </Button>
                          {renderSignedPdfButtons("FR.37")}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {/* Stage 2 Documents Section - Only show for Stage 2 */}
                {isStage2 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2 border-b pb-2">
                    <span className="w-8 h-8 rounded-full bg-orange-600 text-white flex items-center justify-center text-sm font-bold">2</span>
                    2. Aşama Denetim Formları (TR-I)
                  </h3>
                  <div className="space-y-3">
                    {/* FR.04 Audit Report */}
                    <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors border-orange-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">FR.04 Denetim Raporu</p>
                            <p className="text-xs text-gray-500">2. Aşama Denetim Raporu (TR-I)</p>
                            {signedPdfStatus["FR.04"]?.uploaded && (
                              <p className="text-xs text-green-600 mt-1">✓ İmzalı PDF yüklendi</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDocument("FR.04")}
                            disabled={downloadingDoc === "FR.04"}
                            data-testid="download-fr04-word"
                          >
                            {downloadingDoc === "FR.04" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" />Word</>}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => downloadPDF("FR.04")}
                            disabled={downloadingDoc === "FR.04_pdf"}
                            data-testid="download-fr04-pdf"
                          >
                            {downloadingDoc === "FR.04_pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileDown className="w-4 h-4 mr-1" />PDF</>}
                          </Button>
                          {renderSignedPdfButtons("FR.04")}
                        </div>
                      </div>
                    </div>

                    {/* FR.05 Audit Notification */}
                    <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors border-orange-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <Send className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">FR.05 Denetim Bilgilendirme</p>
                            <p className="text-xs text-gray-500">Denetim Bilgilendirme Formu (TR-I)</p>
                            {signedPdfStatus["FR.05"]?.uploaded && (
                              <p className="text-xs text-green-600 mt-1">✓ İmzalı PDF yüklendi</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDocument("FR.05")}
                            disabled={downloadingDoc === "FR.05"}
                            data-testid="download-fr05-word"
                          >
                            {downloadingDoc === "FR.05" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" />Word</>}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => downloadPDF("FR.05")}
                            disabled={downloadingDoc === "FR.05_pdf"}
                            data-testid="download-fr05-pdf"
                          >
                            {downloadingDoc === "FR.05_pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileDown className="w-4 h-4 mr-1" />PDF</>}
                          </Button>
                          {renderSignedPdfButtons("FR.05")}
                        </div>
                      </div>
                    </div>

                    {/* FR.06 Meeting Minutes */}
                    <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors border-orange-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                            <Users className="w-5 h-5 text-yellow-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">FR.06 Toplantı Tutanağı</p>
                            <p className="text-xs text-gray-500">Açılış-Kapanış Toplantı Tutanağı (TR-I)</p>
                            {signedPdfStatus["FR.06"]?.uploaded && (
                              <p className="text-xs text-green-600 mt-1">✓ İmzalı PDF yüklendi</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDocument("FR.06")}
                            disabled={downloadingDoc === "FR.06"}
                            data-testid="download-fr06-word"
                          >
                            {downloadingDoc === "FR.06" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" />Word</>}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => downloadPDF("FR.06")}
                            disabled={downloadingDoc === "FR.06_pdf"}
                            data-testid="download-fr06-pdf"
                          >
                            {downloadingDoc === "FR.06_pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileDown className="w-4 h-4 mr-1" />PDF</>}
                          </Button>
                          {renderSignedPdfButtons("FR.06")}
                        </div>
                      </div>
                    </div>

                    {/* FR.07 NCR Form */}
                    <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors border-orange-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                            <AlertOctagon className="w-5 h-5 text-red-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">FR.07 Uygunsuzluk Bildirimi</p>
                            <p className="text-xs text-gray-500">Uygunsuzluk Bildirim Formu (TR-I)</p>
                            {signedPdfStatus["FR.07"]?.uploaded && (
                              <p className="text-xs text-green-600 mt-1">✓ İmzalı PDF yüklendi</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDocument("FR.07")}
                            disabled={downloadingDoc === "FR.07"}
                            data-testid="download-fr07-word"
                          >
                            {downloadingDoc === "FR.07" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" />Word</>}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => downloadPDF("FR.07")}
                            disabled={downloadingDoc === "FR.07_pdf"}
                            data-testid="download-fr07-pdf"
                          >
                            {downloadingDoc === "FR.07_pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileDown className="w-4 h-4 mr-1" />PDF</>}
                          </Button>
                          {renderSignedPdfButtons("FR.07")}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {/* Signatures Section - Hidden completely as signed PDFs are uploaded directly */}
                {false && (
                <div className="mt-6 border-t pt-6">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Pencil className="w-5 h-5" />
                    Dijital İmzalar
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    İmzalı PDF'leri sisteme yüklemek için ilgili formun yanındaki "İmzalı Yükle" butonunu kullanabilirsiniz.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <SignaturePad
                      label="Ekip Lideri"
                      signatureData={signatures.team_leader}
                      onSave={(sig) => handleSignatureSave("team_leader", sig)}
                      disabled={savingSignatures}
                    />
                    <SignaturePad
                      label="Denetçi"
                      signatureData={signatures.auditor}
                      onSave={(sig) => handleSignatureSave("auditor", sig)}
                      disabled={savingSignatures}
                    />
                    <SignaturePad
                      label="Müşteri Temsilcisi"
                      signatureData={signatures.client_representative}
                      onSave={(sig) => handleSignatureSave("client_representative", sig)}
                      disabled={savingSignatures}
                    />
                  </div>
                </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Floating Save Button */}
      <div className="fixed bottom-6 right-6 flex gap-2">
        {/* Save Button */}
        <Button 
          size="lg" 
          onClick={() => {
            if (activeTab === "checklist") saveChecklist();
            else if (activeTab === "report") saveReport();
            else if (activeTab === "ncr") saveNcrData();
            else if (activeTab === "interviews") saveReport();
            else if (activeTab === "meetings") saveReport();
            else if (activeTab === "evidence") saveReport();
          }}
          disabled={saving}
          style={{ backgroundColor: primaryColor }}
          className="text-white shadow-lg"
        >
          {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
          Kaydet
        </Button>
      </div>

      {/* Photo View Modal */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedPhoto?.filename || "Fotoğraf"}</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {selectedPhoto?.data ? (
              <img 
                src={selectedPhoto.data.startsWith('data:') 
                  ? selectedPhoto.data 
                  : `data:${selectedPhoto.content_type || 'image/jpeg'};base64,${selectedPhoto.data}`
                } 
                alt={selectedPhoto.filename} 
                className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
              />
            ) : (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            )}
            {selectedPhoto?.criteria_id && (
              <p className="mt-2 text-sm text-gray-500">Kriter: {selectedPhoto.criteria_id}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Copy from Previous Report Dialog */}
      <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5" />
              Önceki Rapordan Kopyala
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Herhangi bir müşterinin önceki onaylı {isStage2 ? "Stage 2" : "Stage 3"} denetimlerinden checklist maddelerini (kanıtlar, notlar, çeviriler) ve FR.10 bölümlerini (2-12) kopyalayabilirsiniz.
            </p>
            
            {/* Customer selection */}
            <div className="space-y-2">
              <Label>Müşteri Seçin</Label>
              <Select 
                value={selectedCopyCustomer} 
                onValueChange={(val) => {
                  setSelectedCopyCustomer(val);
                  setSelectedSourceAudit(null);
                  loadApprovedAudits(val === "all" ? null : val);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Müşteri seçin..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Müşteriler</SelectItem>
                  {allCustomers.map((cust) => (
                    <SelectItem key={cust.customer_id} value={cust.customer_id}>
                      {cust.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Source audit selection */}
            <div className="space-y-2">
              <Label>Kaynak Denetim ({isStage2 ? "Stage 2" : "Stage 3"} denetimleri)</Label>
              {loadingApprovedAudits ? (
                <div className="flex items-center gap-2 p-3 border rounded-lg">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm text-gray-500">Yükleniyor...</span>
                </div>
              ) : approvedAudits.length === 0 ? (
                <div className="p-3 border rounded-lg bg-yellow-50 text-yellow-800 text-sm">
                  {selectedCopyCustomer === "all" 
                    ? `Onaylı ${isStage2 ? "Stage 2" : "Stage 3"} denetimi bulunamadı.`
                    : `Bu müşteri için onaylı ${isStage2 ? "Stage 2" : "Stage 3"} denetimi bulunamadı.`
                  }
                </div>
              ) : (
                <Select value={selectedSourceAudit || ""} onValueChange={setSelectedSourceAudit}>
                  <SelectTrigger>
                    <SelectValue placeholder="Denetim seçin..." />
                  </SelectTrigger>
                  <SelectContent>
                    {approvedAudits.map((audit) => (
                      <SelectItem key={audit.audit_record_id} value={audit.audit_record_id}>
                        {audit.customer_name} - {new Date(audit.created_at).toLocaleDateString('tr-TR')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {/* Copy options */}
            <div className="space-y-3 p-3 border rounded-lg bg-gray-50">
              <Label className="text-sm font-medium">Kopyalanacak Veriler</Label>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="copy-checklist"
                  checked={copyOptions.checklist}
                  onCheckedChange={(checked) => setCopyOptions(prev => ({ ...prev, checklist: checked }))}
                />
                <label htmlFor="copy-checklist" className="text-sm cursor-pointer">
                  Checklist (Kanıtlar, Notlar, Çeviriler)
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="copy-fr10"
                  checked={copyOptions.fr10}
                  onCheckedChange={(checked) => setCopyOptions(prev => ({ ...prev, fr10: checked }))}
                />
                <label htmlFor="copy-fr10" className="text-sm cursor-pointer">
                  FR.10 Bölümleri (2-12)
                </label>
              </div>
            </div>
            
            <div className="p-3 border rounded-lg bg-blue-50 text-blue-800 text-sm">
              <strong>Not:</strong> Kopyalama işlemi mevcut verilerin üzerine yazacaktır. 
              Değerlendirme durumları (Conform/NC) kopyalanmaz, denetçi tarafından yeniden değerlendirilmelidir.
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCopyDialog(false)}>
                İptal
              </Button>
              <Button 
                onClick={copyFromApprovedAudit}
                disabled={!selectedSourceAudit || copying || (!copyOptions.checklist && !copyOptions.fr10)}
              >
                {copying ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Copy className="w-4 h-4 mr-2" />
                )}
                Kopyala
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoyalCertAuditForm;
